import { cn } from './lib/utils';
import { RefreshCcw, ShieldCheck } from 'lucide-react';
import { useMemo, useEffect, useRef, useCallback, useState, Profiler } from 'react';

import SettingsSidebar from './components/layout/SettingsSidebar';
import HeaderTerminal from './components/layout/HeaderTerminal';
import { HistoryLoadingIndicator } from './components/layout/HistoryLoadingIndicator';
import { HistoryMessage } from './components/layout/HistoryMessage';
import { mensagemHistorico, START_PULSE_MS, START_NEAR } from './utils/historyMessage';
import { msgProbe } from './utils/msgProbe';
import { BinanceCandleSource, type CandleErrorKind } from './utils/candleSource';
import { SeriesWindow, intervalToMs } from './utils/seriesWindow';
import { BLOCK, applyNewerBlock, checkSeries, evictionPlan, newerBlockBeforeWithTail, preloadNeed, tailPush, windowLimitFrom, type Direction } from './utils/windowPolicy';
import { isAtLoadedEdge } from './utils/historyNav';
import { AnchoredNav, tagNewestAbs, newestAbsOf, positionFor, targetFor, setTargetFor, setPositionFor, motionOf, INITIAL_HISTORY_KEY, tagSeries, seriesOf } from './utils/anchoredNav';
import { momentoDoCentro, centroParaMomento } from './utils/viewMoment';
import { carregarEmVolta, pesoDoPedido, CargaGrade, type SerieEmVolta } from './utils/seriesAtMoment';
import { navRec, navSource, SRC } from './utils/navRecorder';
import { useP1RenderCommit, p1SeriesSwitch } from './utils/p1Probe';
import { swProbe } from './utils/switchProbe';
import { histStep, histName, histViolations, histRuntime, type HistEvent, type HistMode, type HistState } from './utils/historyMode';
import { replay } from './utils/replayHooks';
import { DebugOverlay } from './components/DebugOverlay';
import { p1T, p1End, S as P1S } from './utils/p1Probe';
import { DBG_ENABLED, dbgLoadStart, dbgLoadDone, dbgEvent, dbgMathCtx, dbgState, dbgAutoplayEnd, isRealRecuo, dbgWindowState, dbgWindowEvict, dbgWindowStitch, dbgWindowNewerLoad, dbgWindowAhead, dbgWindowWait, dbgWindowRecuo, dbgWindowRecuoTroca } from './utils/debugTelemetry';
import { syncCount, SC } from './utils/syncProbe';
import FooterTerminal from './components/layout/FooterTerminal';
import { MainTradingCanvas } from './components/layout/MainTradingCanvas';
import { AuthOverlay } from './components/layout/AuthOverlay';
import { InteractiveHUDControls } from './components/layout/InteractiveHUDControls';
import { AiAnalysisModal } from './components/modals/AiAnalysisModal';
import { EditUserModal } from './components/modals/EditUserModal';
import { ConfirmDialogModal } from './components/modals/ConfirmDialogModal';
import { MarketDataOverlay } from './components/modals/MarketDataOverlay';
import { UserFeedbacksModal } from './components/modals/UserFeedbacksModal';
import { InteractiveModeHUD } from './components/layout/InteractiveModeHUD';
import { BackWall2DChart } from './components/modals/BackWall2DChart';
import { Floor2DChart } from './components/modals/Floor2DChart';
import { SideWall2DChart } from './components/modals/SideWall2DChart';




import { WaveGenerator } from './utils/MarketDataGenerator';

import { processRawBinanceData } from './utils/BinanceService';
import { nextRetryDelayMs } from './utils/loadRetry';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, loginWithGoogle, db } from './lib/firebase';
import { isAdminUser } from './lib/admin';
import RadialMenu from './components/RadialMenu';
import { AsyncFeedbackWidget } from './components/AsyncFeedbackWidget';
import { useAuth } from "./hooks/useAuth";
import { useFirebaseSync } from "./hooks/useFirebaseSync";
import { useMarketStream } from "./hooks/useMarketStream";
import { NAV_VISIBLE_CANDLES } from './utils/historyNav';
import { useTradingState } from "./hooks/useTradingState";
import { useFlowaesStore } from './store/useFlowaesStore';
import { useTerminalSettings } from "./hooks/useTerminalSettings";
import { GodPanel } from "./components/layout/GodPanel";
import { MarketsPanel } from "./components/layout/MarketsPanel";
import { appProfiler } from './utils/appProfiler';

// F3a: o teto fixo de 10.000 velas (que marcava "fim" por engano) virou o limite da janela deslizante
// (src/utils/windowPolicy.ts: padrão 10.000, ?janela= com ?dbg=1). F0: a pré-carga liga ao armar a navegação e em
// ocioso; quanto carregar à frente fica em windowPolicy (3 blocos na direção do movimento, 5 parado).
const PRELOAD_IDLE_MS = 2000;
// Velas de aquecimento que o caminho completo do processamento descarta (espelho de math-processor.ts:448-450).
// O caminho rápido (tick ao vivo) não descarta, então o total processado oscila entre N e N−50.
const MATH_WARMUP_DROP = 50;
// F1 C4: a carga inicial pede as velas da janela visível MAIS o aquecimento do indicador, para a série processada já
// nascer com VISIBLE (1.000) velas. Antes pedia 1.000 e saíam 950: por 2–3 quadros o espaçamento ficava ~5% errado
// até a primeira carga de histórico (alarme de Δx ≈ 50 na sonda de dessincronia). O cálculo e o aquecimento não mudam.
const INITIAL_CANDLES = NAV_VISIBLE_CANDLES + MATH_WARMUP_DROP;
// F3b C2: bloco da série nova em volta de T (troca no mesmo momento): 1.025 velas de cada lado depois do aquecimento,
// para a vela de T caber no centro com folga (com 1.050 sobravam exatamente as 1.000 visíveis e o centro encostava).
// O servidor pagina em lotes de 1.000 (server.ts).
const AROUND_CANDLES = 2 * NAV_VISIBLE_CANDLES + MATH_WARMUP_DROP;

export default function App() {
  syncCount(SC.APP_RENDER); // diagnóstico (?dbg=1): contador de renders do App
  useP1RenderCommit(); // P1b T6(b): render → commit do App (?dbg=1; sem a flag não usa hook)
    const authHook = useAuth();
    const { authStatus, handleLogout, setAuthStatus, authError, setAuthError, authToken, setAuthToken, isAuthLoading, setIsAuthLoading, user, isSubscriptionActive, setIsSubscriptionActive, showLogoutConfirm, setShowLogoutConfirm, showLoginLogoutConfirm, setShowLoginLogoutConfirm, isSaving, setIsSaving, saveMessage, setSaveMessage, isFirestoreOffline, setIsFirestoreOffline, editingSystemUser, setEditingSystemUser, confirmDialog, setConfirmDialog, userFeedbacksModalId, setUserFeedbacksModalId, leadName, setLeadName, leadEmail, setLeadEmail, leadPhone, setLeadPhone, leadCompany, setLeadCompany, leadDetails, setLeadDetails, isSubmittingLead, leadSuccessMsg, setLeadSuccessMsg, handleLogin, fetchFeedbacks, fetchSystemUsers, fetchLeads, handleSaveUserEdit, submitLead, fetchAdminMetrics, feedbacksList, feedbackToDelete, setFeedbackToDelete, confirmDeleteFeedback, setIsSavingBrand, setBrandSaveSuccess, setBrandSaveError } = authHook;
    const { useRealData, setUseRealData, symbol, setSymbol, isEditingSymbol, setIsEditingSymbol, realData, setRealData, latencyMs, setLatencyMs, isLoadingData, setIsLoadingData, dataError, setDataError, historyOffset: historyKey, setHistoryOffset: setHistoryKey, isReturningToLive, setIsReturningToLive, isLoadingHistory, setIsLoadingHistory, hasReachedHistoryEnd, setHasReachedHistoryEnd } = useMarketStream();
    const { radialMenuPos, setRadialMenuPos, zones, setZones, selectedZoneId, setSelectedZoneId, showMarketsPanel, setShowMarketsPanel, favorites, setFavorites, activeMarketCategory, setActiveMarketCategory, showMobileSettings, setShowMobileSettings, activeSettingsTab, setActiveSettingsTab, showMobileData, setShowMobileData, isCrosshairActive, setIsCrosshairActive, cameraTweenTarget, setCameraTweenTarget, controlsTweenTarget, setControlsTweenTarget, isAIPanelOpen, setIsAIPanelOpen, aiAnalysis, setAiAnalysis, isAiLoading, setIsAiLoading } = useTradingState();
    
  const showGodPanel = useFlowaesStore(s => s.showGodPanel);
  const setShowGodPanel = useFlowaesStore(s => s.setShowGodPanel);
  const setActiveGesture = useFlowaesStore(s => s.setActiveGesture);
  const incrementGestureVersion = useFlowaesStore(s => s.incrementGestureVersion);
  
  const [isBackWallChartOpen, setIsBackWallChartOpen] = useState(false);
  const [isFloorChartOpen, setIsFloorChartOpen] = useState(false);
  const [isSideChartOpen, setIsSideChartOpen] = useState(false);

  const terminalSettings = useTerminalSettings();
  const { showSMA10, setShowSMA10, showSMA20, setShowSMA20, showSMA50, setShowSMA50, candleGraphDistance3D, setCandleGraphDistance3D, floorDistance3D, setFloorDistance3D, sideGraphDistance3D, setSideGraphDistance3D, width3D, setWidth3D, heightScale3D, setHeightScale3D, zLimit3D, setZLimit3D, sma10Period, setSma10Period, sma20Period, setSma20Period, sma50Period, setSma50Period, gridColor, setGridColor, gridOpacity, setGridOpacity, gridThickness, setGridThickness, showGrid, setShowGrid, edgesColor, setEdgesColor, edgesOpacity, setEdgesOpacity, buyersColor, setBuyersColor, sellersColor, setSellersColor, titleSize, setTitleSize, floorLabelsSize, setFloorLabelsSize, sideLabelsSize, setSideLabelsSize, floorLabelsSpread, setFloorLabelsSpread, sideLabelsSpread, setSideLabelsSpread, floorLabelsOpacity, setFloorLabelsOpacity, sideLabelsOpacity, setSideLabelsOpacity, timeLabelsSize, setTimeLabelsSize, timeLabelsOpacity, setTimeLabelsOpacity, momentumLabelsSize, setMomentumLabelsSize, momentumLabelsOpacity, setMomentumLabelsOpacity, periodLabelsSize, setPeriodLabelsSize, periodLabelsOpacity, setPeriodLabelsOpacity, priceLabelsSize, setPriceLabelsSize, priceLabelsOpacity, setPriceLabelsOpacity, connectorsOpacity, setConnectorsOpacity, connectorsThickness, setConnectorsThickness, showConnectors, setShowConnectors, periodColor, setPeriodColor, periodOpacity, setPeriodOpacity, showPeriodSeparators, setShowPeriodSeparators, periodLineStyle, setPeriodLineStyle, periodLineWidth, setPeriodLineWidth, showPeriodLabels, setShowPeriodLabels, curveTension, setCurveTension, backdropBlur, setBackdropBlur, floorChartOpacity, setFloorChartOpacity, sideChartOpacity, setSideChartOpacity, backChartOpacity, setBackChartOpacity, floorSmaOpacity, setFloorSmaOpacity, sideSmaOpacity, setSideSmaOpacity, backSmaOpacity, setBackSmaOpacity, main3DOpacity, setMain3DOpacity, sma103DOpacity, setSma103DOpacity, sma203DOpacity, setSma203DOpacity, sma503DOpacity, setSma503DOpacity, floorChartLineWidth, setFloorChartLineWidth, sideChartLineWidth, setSideChartLineWidth, backSmaLineWidth, setBackSmaLineWidth, floorSmaLineWidth, setFloorSmaLineWidth, sideSmaLineWidth, setSideSmaLineWidth, radialMenuColor, setRadialMenuColor, buttonBaseColor, setButtonBaseColor, timeframe, setTimeframe, visibleTimeframes, setVisibleTimeframes, controlRotateSpeed, setControlRotateSpeed, controlPanSpeed, setControlPanSpeed, controlZoomSpeed, setControlZoomSpeed, show3D, setShow3D, backwallStyle, setBackwallStyle, showShadowFloor, setShowShadowFloor, showShadowSide, setShowShadowSide, showMidLineWall, setShowMidLineWall, midLineWallColor, setMidLineWallColor, midLineWallOpacity, setMidLineWallOpacity, sideHistoryShadowStrength, setSideHistoryShadowStrength, historyShadowStrength, setHistoryShadowStrength, lockedSliders, setLockedSliders, hasInteracted, setHasInteracted, isInitialGlideComplete, setIsInitialGlideComplete, isCurrentlyIdle, setIsCurrentlyIdle, uiHiddenLevel, setUiHiddenLevel, isEyeFlashing, setIsEyeFlashing, idleTimerRef, colorUp, setColorUp, colorDown, setColorDown, colorIntensity, setColorIntensity, colorContrast, setColorContrast, momentumSensitivity, setMomentumSensitivity, lineWidth3D, setLineWidth3D, autoRotate, setAutoRotate, interactionMode, setInteractionMode, timeSensitivity, setTimeSensitivity, returnSensitivity, setReturnSensitivity, cameraViewMode, setCameraViewMode, cameraStates, setCameraStates, activeGodTab, showPerfPanel, setIsDraggingGodSlider } = terminalSettings;

  useFirebaseSync(user, {
      setBackChartOpacity,
      setBackdropBlur,
      setBackSmaLineWidth,
      setBackSmaOpacity,
      setBackwallStyle,
      setButtonBaseColor,
      setBuyersColor,
      setCameraStates,
      setCandleGraphDistance3D,
      setColorContrast,
      setColorDown,
      setColorIntensity,
      setColorUp,
      setConnectorsOpacity,
      setConnectorsThickness,
      setControlPanSpeed,
      setControlRotateSpeed,
      setControlZoomSpeed,
      setCurveTension,
      setEdgesColor,
      setEdgesOpacity,
      setFavorites,
      setFloorChartLineWidth,
      setFloorChartOpacity,
      setFloorDistance3D,
      setFloorLabelsOpacity,
      setFloorLabelsSize,
      setFloorLabelsSpread,
      setFloorSmaLineWidth,
      setFloorSmaOpacity,
      setGridColor,
      setGridOpacity,
      setGridThickness,
      setHeightScale3D,
      setIsAuthLoading,
      setIsSubscriptionActive,
      setLineWidth3D,
      setLockedSliders,
      setMain3DOpacity,
      setMidLineWallColor,
      setMidLineWallOpacity,
      setMomentumLabelsOpacity,
      setMomentumLabelsSize,
      setMomentumSensitivity,
      setPeriodColor,
      setPeriodLabelsOpacity,
      setPeriodLabelsSize,
      setPeriodLineStyle,
      setPeriodLineWidth,
      setPeriodOpacity,
      setPriceLabelsOpacity,
      setPriceLabelsSize,
      setRadialMenuColor,
      setSellersColor,
      setShow3D,
      setShowConnectors,
      setShowGrid,
      setShowMidLineWall,
      setShowPeriodLabels,
      setShowPeriodSeparators,
      setShowShadowFloor,
      setShowShadowSide,
      setShowSMA10,
      setShowSMA20,
      setShowSMA50,
      setSideChartLineWidth,
      setSideChartOpacity,
      setSideGraphDistance3D,
      setSideLabelsOpacity,
      setSideLabelsSize,
      setSideLabelsSpread,
      setSideSmaLineWidth,
      setSideSmaOpacity,
      setSma103DOpacity,
      setSma10Period,
      setSma203DOpacity,
      setSma20Period,
      setSma503DOpacity,
      setSma50Period,
      setSymbol,
      setTimeframe,
      setTimeLabelsOpacity,
      setTimeLabelsSize,
      setTitleSize,
      setUseRealData,
      setVisibleTimeframes,
      setWidth3D,
      setZLimit3D
    });

  // Keep user presence updated while they are active/online
  useEffect(() => {
    if (!showGodPanel) {
      setIsDraggingGodSlider(false);
      return;
    }
    const handlePointerUp = () => setIsDraggingGodSlider(false);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [showGodPanel]);
  useEffect(() => {
    const handleRecapture = (e: any) => {
      setUiHiddenLevel(e.detail ? 2 : 0);
    };
    window.addEventListener('feedback-recapture', handleRecapture);
    return () => window.removeEventListener('feedback-recapture', handleRecapture);
  }, []);
  const lastPinchTimeRef = useRef<number>(0);
  const lastTapRef = useRef<number>(0);
  const radialMenuTimerRef = useRef<any>(null);
  const radialMenuStartPosRef = useRef<{x: number, y: number} | null>(null);
  const isPointerDownRef = useRef(false);
  const lastTapPosRef = useRef<{ x: number, y: number } | null>(null);
  const touchStartPosRef = useRef<{ x: number, y: number } | null>(null);
  const touchStartTimeRef = useRef<number>(0);
  const lastManualUiShowTimeRef = useRef<number>(0);
  const isCurrentlyIdleRef = useRef(false);
  useEffect(() => {
    isCurrentlyIdleRef.current = isCurrentlyIdle;
  }, [isCurrentlyIdle]);

  const lastTimerResetRef = useRef(0);

  const resetIdleTimer = useCallback((isZoom = false) => {
    const now = Date.now();
    if (now - lastTimerResetRef.current < 250) return;
    lastTimerResetRef.current = now;

    if (isCurrentlyIdleRef.current) {
      if (!isZoom) {
        setUiHiddenLevel(0);
        lastManualUiShowTimeRef.current = Date.now();
      }
    }
    setIsCurrentlyIdle(false);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    if (isInitialGlideComplete) {
      idleTimerRef.current = setTimeout(() => {
        setIsCurrentlyIdle(true);
      }, 30000); // 30 seconds of absolute idle before starting autoRotate
    }
  }, [isInitialGlideComplete]);

  
  const handleOpenMidWallChart = useCallback(() => { setIsReturningToLive(false); setInteractionMode('orbit'); setHistoryOffset(prev => { navSource(SRC.JANELA2D); targetHistoryOffset.current = prev; navSource(0); return prev; }); }, []);
  const handleOpenBackWallChart = useCallback(() => { setIsBackWallChartOpen(true); setIsReturningToLive(false); setInteractionMode('orbit'); setHistoryOffset(prev => { navSource(SRC.JANELA2D); targetHistoryOffset.current = prev; navSource(0); return prev; }); }, []);
  const handleOpenFloorChart = useCallback(() => { setIsFloorChartOpen(true); setIsReturningToLive(false); setInteractionMode('orbit'); setHistoryOffset(prev => { navSource(SRC.JANELA2D); targetHistoryOffset.current = prev; navSource(0); return prev; }); }, []);
  const handleOpenSideChart = useCallback(() => { setIsSideChartOpen(true); setIsReturningToLive(false); setInteractionMode('orbit'); setHistoryOffset(prev => { navSource(SRC.JANELA2D); targetHistoryOffset.current = prev; navSource(0); return prev; }); }, []);

  const handleUserInteraction = useCallback((e?: Event) => {
    if (e) {
      if (e.target instanceof Element && e.target.closest('.ignore-interaction')) {
        return;
      }
      if (e.type === 'wheel') {
        resetIdleTimer(true);
        return; // Handled as zoom
      }
      if (e.type === 'touchstart' || e.type === 'touchmove' || e.type === 'touchend') {
        const touchEvent = e as TouchEvent;
        if (touchEvent.touches && touchEvent.touches.length > 1) {
          lastPinchTimeRef.current = Date.now();
          resetIdleTimer(true);
          return; // Handled as pinch zoom
        }
        // If we were pinching less than 500ms ago, ignore this touch event too
        // as the user might be lifting fingers unevenly
        if (Date.now() - lastPinchTimeRef.current < 500) {
          
          resetIdleTimer();
          return;
        }
      }
      if (e.type === 'mousemove') {

        const mouseEvent = e as MouseEvent;
        if (mouseEvent.buttons === 0) return;
      }
    }

    resetIdleTimer();
    setIsInitialGlideComplete(true);
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    // Cancel active camera tween on user interaction
    setCameraTweenTarget(prev => {
      if (prev !== null) {
        return null;
      }
      return null;
    });
    setControlsTweenTarget(null);
  }, [resetIdleTimer, hasInteracted]);

  useEffect(() => {
    resetIdleTimer();

    window.addEventListener('wheel', handleUserInteraction, { passive: true, capture: true });
    window.addEventListener('click', handleUserInteraction, { passive: true, capture: true });
    window.addEventListener('mousemove', handleUserInteraction, { passive: true, capture: true }); // Desktop wake up
    window.addEventListener('touchstart', handleUserInteraction, { passive: true, capture: true });
    window.addEventListener('touchmove', handleUserInteraction, { passive: true, capture: true });
    window.addEventListener('touchend', handleUserInteraction, { passive: true, capture: true });

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      window.removeEventListener('wheel', handleUserInteraction, { capture: true } as any);
      window.removeEventListener('click', handleUserInteraction, { capture: true } as any);
      window.removeEventListener('mousemove', handleUserInteraction, { capture: true } as any);
      window.removeEventListener('touchstart', handleUserInteraction, { capture: true } as any);
      window.removeEventListener('touchmove', handleUserInteraction, { capture: true } as any);
      window.removeEventListener('touchend', handleUserInteraction, { capture: true } as any);
    };
  }, [isInitialGlideComplete, handleUserInteraction]);
  useEffect(() => {
    localStorage.setItem('priceZones', JSON.stringify(zones));
  }, [zones]);
  const isAdmin = isAdminUser(user);
  // Leads State
  // Feedbacks State
  const isCrosshairPinned = false;
  const crosshairRef = useRef<{ setIndex: (i: number) => void }>(null);
  const saveCameraState = useCallback((tf: string, position: [number, number, number], target: [number, number, number]) => {
    setCameraStates((prev: any) => {
      const next = {
        ...prev,
        [tf]: { position, target }
      };
      localStorage.setItem('cameraStates', JSON.stringify(next));
      return next;
    });
  }, []);
  useEffect(() => {
    if (!useRealData && localStorage.getItem('useRealData') === null) {
      setUseRealData(true);
    }
  }, [useRealData]);
  const isLoadingDataRef = useRef(false);
  // AI State
  const requestAiAnalysis = async (currentData = processedData) => {
    setIsAiLoading(true);
    setAiAnalysis(null);
    try {
      const recentPrices = currentData.slice(-10).map(d => Number((d.close ?? d.price).toFixed(2)));
      const latestPrice = recentPrices[recentPrices.length - 1] || 0;
      const currentMomentum = currentData[currentData.length - 1]?.imf || 0;
      
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
           symbol,
           timeframe,
           latestPrice,
           recentPrices,
           currentMomentum
        })
      });
      
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = null;
        }
        throw new Error(errorData?.error || `Erro na API: ${response.status}`);
      }
      const data = await response.json();
      setAiAnalysis(data.analysis);
    } catch (e: any) {
       console.error("AI Err:", e);
       setAiAnalysis(`Desculpe, ocorreu um erro ao gerar a análise: ${e.message}`);
    } finally {
      setIsAiLoading(false);
    }
  };

  useEffect(() => {
     if (isAIPanelOpen && !aiAnalysis && !isAiLoading && processedData.length > 0) {
        requestAiAnalysis();
     }
  }, [isAIPanelOpen, symbol, timeframe]);

  // F3a C1: as velas brutas da série aberta ficam numa SeriesWindow (índice ancorado, despejo por blocos). Uma
  // instância por série: trocar de ativo/timeframe cria outra (é a guarda de série das cargas em andamento).
  const seriesRef = useRef<SeriesWindow | null>(null);
  // F3a C2: limite da janela (velas brutas), se a janela contém o presente (as velas novas ao vivo entram nela) e o
  // horário da primeira vela da fonte, quando já se sabe (fimDosDados / vazio).
  const windowLimitRef = useRef(windowLimitFrom(typeof window !== 'undefined' ? window.location.search : '', DBG_ENABLED));
  const [windowAtPresent, setWindowAtPresentState] = useState(true);
  const windowAtPresentRef = useRef(true);
  const setWindowAtPresent = (v: boolean) => { if (navRec) navRec.atPresent = v; if (windowAtPresentRef.current !== v) { windowAtPresentRef.current = v; setWindowAtPresentState(v); } };
  const knownStartRef = useRef<number | null>(null);
  // F3a C3: cauda ao vivo (velas brutas do WebSocket com o presente fora da janela; contígua, até 1 bloco)
  const tailRef = useRef<any[][]>([]);
  /** direção do último movimento da vista (-1 passado, +1 presente), para a pré-carga */
  const moveDirRef = useRef<Direction>(0);
  const activeIntervalRef = useRef<string>('1d');
  const pointsNeedUpdateRef = useRef<boolean>(false);

  enum OperationType {
    CREATE = 'create',
    UPDATE = 'update',
    DELETE = 'delete',
    LIST = 'list',
    GET = 'get',
    WRITE = 'write',
  }

  interface FirestoreErrorInfo {
    error: string;
    operationType: OperationType;
    path: string | null;
    authInfo: {
      userId?: string | null;
      email?: string | null;
      emailVerified?: boolean | null;
      isAnonymous?: boolean | null;
      tenantId?: string | null;
      providerInfo?: {
        providerId?: string | null;
        email?: string | null;
      }[];
    }
  }

  const handleFirestoreError = (error: unknown, operationType: OperationType, path: string | null) => {
    const message = error instanceof Error ? error.message : String(error);
    const isOffline = message.includes('offline') || message.includes('unavailable');
    
    const errInfo: FirestoreErrorInfo = {
      error: message,
      authInfo: {
        userId: auth.currentUser?.uid,
        email: auth.currentUser?.email,
        emailVerified: auth.currentUser?.emailVerified,
        isAnonymous: auth.currentUser?.isAnonymous,
        tenantId: auth.currentUser?.tenantId,
        providerInfo: auth.currentUser?.providerData?.map(provider => ({
          providerId: provider.providerId,
          email: provider.email,
        })) || []
      },
      operationType,
      path
    };

    if (isOffline) {
      console.warn('Firestore is offline or unavailable. Using local state. ', JSON.stringify(errInfo));
      setIsFirestoreOffline(true);
      // Don't throw for offline errors to keep the app functional in simulation mode
      return;
    }

    console.error('Firestore Error (falling back to Local Storage): ', JSON.stringify(errInfo));
    setIsFirestoreOffline(true);
  };
  


  useEffect(() => {
    document.documentElement.style.setProperty('--radial-menu-color', radialMenuColor);
    document.documentElement.style.setProperty('--button-base-color', buttonBaseColor);
    document.documentElement.style.setProperty('--backdrop-blur', `${backdropBlur}px`);
  }, [radialMenuColor, buttonBaseColor, backdropBlur]);

  const isCreator = isAdminUser(user);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isCreator && e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        setShowGodPanel(!showGodPanel);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCreator]);
  useEffect(() => {
    if (isAdmin && showGodPanel) {
      if (activeGodTab === 'dashboard') {
        fetchAdminMetrics();
      }
      if (activeGodTab === 'users') {
        fetchSystemUsers();
        fetchLeads();
      }
      if (activeGodTab === 'feedbacks') {
        fetchFeedbacks();
      }
    }
  }, [activeGodTab, showGodPanel, isAdmin]);

  useEffect(() => {
    if (window.location.pathname === '/dev-admin' && isAdmin) {
      setShowGodPanel(true);
    }
  }, [isAdmin]);

  const adminDefaultsRef = useRef<any>(null);
  const resetSettingsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    try {
      const brandDocRef = doc(db, 'admin_settings', 'visual_identity');
      const unsub = onSnapshot(brandDocRef, (snap: any) => {
        if (snap.exists()) {
          const data = snap.data();
          adminDefaultsRef.current = data;
          localStorage.setItem('flowaes_admin_global_defaults', JSON.stringify(data));
          
          if (!localStorage.getItem('flowaes_user_initialized')) {
            localStorage.setItem('flowaes_user_initialized', 'true');
            if (resetSettingsRef.current) resetSettingsRef.current();
          }
        }
      }, (err: any) => {
        console.warn("Could not listen to admin_settings/visual_identity:", err);
      });
      return () => unsub();
    } catch (e) {
      console.warn("Error setting up listener for admin_settings/visual_identity:", e);
    }
  }, []);

  const handleSaveBrandIdentity = async () => {
    setIsSavingBrand(true);
    setBrandSaveSuccess(false);
    setBrandSaveError("");
    
    if (!isAdmin) {
        setBrandSaveError("Acesso Negado. Ative a Chave de Acesso Admin ou use a Conta Google autorizada.");
        setIsSavingBrand(false);
        return;
    }

    try {
      const payload = {
        show3D,
        backwallStyle,
        showShadowFloor,
        showShadowSide,
        showMidLineWall,
        midLineWallColor,
        midLineWallOpacity,
        sideHistoryShadowStrength,
        historyShadowStrength,
        colorUp,
        colorDown,
        colorIntensity,
        colorContrast,
        momentumSensitivity,
        lineWidth3D,
        useRealData,
        showSMA10,
        showSMA20,
        showSMA50,
        candleGraphDistance3D,
        floorDistance3D,
        sideGraphDistance3D,
        width3D,
        heightScale3D,
        zLimit3D,
        showGrid,
        showConnectors,
        connectorsOpacity,
        connectorsThickness,
        gridColor,
        gridOpacity,
        gridThickness,
        edgesColor,
        edgesOpacity,
        buyersColor,
        sellersColor,
        floorLabelsOpacity, sideLabelsOpacity,
        titleSize,
        floorLabelsSize, sideLabelsSize, floorLabelsSpread, sideLabelsSpread,
        timeLabelsSize,
        timeLabelsOpacity,
        momentumLabelsSize,
        momentumLabelsOpacity,
        priceLabelsSize,
        priceLabelsOpacity,
        periodLabelsSize,
        periodLabelsOpacity,
        showPeriodSeparators,
        periodColor,
        periodOpacity,
        sma10Period,
        sma20Period,
        sma50Period,
        periodLineStyle,
        periodLineWidth,
        showPeriodLabels,
        curveTension,
        floorChartOpacity,
        sideChartOpacity,
        backChartOpacity,
        floorChartLineWidth,
        sideChartLineWidth,
        floorSmaOpacity,
        sideSmaOpacity,
        backSmaOpacity,
        main3DOpacity,
        sma103DOpacity,
        sma203DOpacity,
        sma503DOpacity,
        backSmaLineWidth,
        floorSmaLineWidth,
        sideSmaLineWidth,
        backdropBlur,
        radialMenuColor,
        buttonBaseColor,
        lockedSliders,
        controlRotateSpeed,
        controlPanSpeed,
        controlZoomSpeed,
        updatedBy: user?.email || "admin",
        updatedAt: new Date().toISOString()
      };

      const brandDocRef = doc(db, 'admin_settings', 'visual_identity');
      await setDoc(brandDocRef, payload);
      adminDefaultsRef.current = payload;
      localStorage.setItem('flowaes_admin_global_defaults', JSON.stringify(payload));
      setBrandSaveSuccess(true);
      setTimeout(() => setBrandSaveSuccess(false), 4000);
    } catch (e: any) {
      console.error("Error saving brand identity to Firestore:", e);
      if (e.code === 'permission-denied') {
        setBrandSaveError("Erro de permissão no Firebase. O salvamento global na nuvem requer login direto com a conta Google autorizada (marketing.inteligente2026@gmail.com). Suas alterações atuais estão salvas localmente nesta sessão!");
      } else {
        setBrandSaveError(e.message || "Acesso Negado.");
      }
    } finally {
      setIsSavingBrand(false);
    }
  };

  const toggleFavorite = async (marketSymbol: string) => {
    let newFavs;
    const currentFavs = Array.isArray(favorites) ? favorites : [];
    if (currentFavs.includes(marketSymbol)) {
      newFavs = currentFavs.filter(s => s !== marketSymbol);
    } else {
      newFavs = [...currentFavs, marketSymbol];
    }
    setFavorites(newFavs);
    localStorage.setItem('favorites', JSON.stringify(newFavs));

    if (user) {
      try {
        const docRef = doc(db, 'users', user.uid);
        await setDoc(docRef, { favorites: newFavs }, { merge: true });
      } catch (e) {
        console.error("Error saving favorites", e);
        handleFirestoreError(e, OperationType.UPDATE, `users/${user.uid}/favorites`);
      }
    }
  };

  // Auto-save to localStorage
  useEffect(() => {
    localStorage.setItem('timeframe', timeframe);
    localStorage.setItem('controlRotateSpeed', String(controlRotateSpeed));
    localStorage.setItem('controlPanSpeed', String(controlPanSpeed));
    localStorage.setItem('controlZoomSpeed', String(controlZoomSpeed));
    localStorage.setItem('show3D', JSON.stringify(show3D));
    localStorage.setItem('backwallStyle', backwallStyle);
    localStorage.setItem('showShadowFloor', JSON.stringify(showShadowFloor));
    localStorage.setItem('showShadowSide', JSON.stringify(showShadowSide));
    localStorage.setItem('showMidLineWall', JSON.stringify(showMidLineWall));
    localStorage.setItem('midLineWallColor', midLineWallColor);
    localStorage.setItem('midLineWallOpacity', String(midLineWallOpacity));
    localStorage.setItem('sideHistoryShadowStrength', String(sideHistoryShadowStrength));
    localStorage.setItem('historyShadowStrength', String(historyShadowStrength));
    localStorage.setItem('colorUp', colorUp);
    localStorage.setItem('colorDown', colorDown);
    localStorage.setItem('colorIntensity', String(colorIntensity));
    localStorage.setItem('colorContrast', String(colorContrast));
    localStorage.setItem('momentumSensitivity', String(momentumSensitivity));
    localStorage.setItem('lineWidth3D', String(lineWidth3D));
    localStorage.setItem('useRealData', JSON.stringify(useRealData));
    localStorage.setItem('symbol', symbol);
    localStorage.setItem('visibleTimeframes', JSON.stringify(visibleTimeframes));
    localStorage.setItem('showSMA10', JSON.stringify(showSMA10));
    localStorage.setItem('showSMA20', JSON.stringify(showSMA20));
    localStorage.setItem('showSMA50', JSON.stringify(showSMA50));
    localStorage.setItem('candleGraphDistance3D', String(candleGraphDistance3D));
    localStorage.setItem('floorDistance3D', String(floorDistance3D));
    localStorage.setItem('sideGraphDistance3D', String(sideGraphDistance3D));
    localStorage.setItem('width3D', String(width3D));
    localStorage.setItem('heightScale3D', String(heightScale3D));
    localStorage.setItem('zLimit3D', String(zLimit3D));
    localStorage.setItem('showGrid', JSON.stringify(showGrid));
    localStorage.setItem('showConnectors', JSON.stringify(showConnectors));
    localStorage.setItem('connectorsOpacity', String(connectorsOpacity));
    localStorage.setItem('connectorsThickness', String(connectorsThickness));
    localStorage.setItem('gridColor', gridColor);
    localStorage.setItem('gridOpacity', String(gridOpacity));
    localStorage.setItem('gridThickness', String(gridThickness));
    localStorage.setItem('edgesColor', edgesColor);
    localStorage.setItem('edgesOpacity', String(edgesOpacity));
    localStorage.setItem('buyersColor', buyersColor);
    localStorage.setItem('sellersColor', sellersColor);
    localStorage.setItem('floorLabelsOpacity', String(floorLabelsOpacity));
    localStorage.setItem('sideLabelsOpacity', String(sideLabelsOpacity));
    localStorage.setItem('titleSize', String(titleSize));
    localStorage.setItem('floorLabelsSize', String(floorLabelsSize));
    localStorage.setItem('sideLabelsSize', String(sideLabelsSize));
    localStorage.setItem('floorLabelsSpread', String(floorLabelsSpread));
    localStorage.setItem('sideLabelsSpread', String(sideLabelsSpread));
    localStorage.setItem('timeLabelsSize', String(timeLabelsSize));
    localStorage.setItem('timeLabelsOpacity', String(timeLabelsOpacity));
    localStorage.setItem('momentumLabelsSize', String(momentumLabelsSize));
    localStorage.setItem('momentumLabelsOpacity', String(momentumLabelsOpacity));
    localStorage.setItem('priceLabelsSize', String(priceLabelsSize));
    localStorage.setItem('priceLabelsOpacity', String(priceLabelsOpacity));
    localStorage.setItem('periodLabelsSize', String(periodLabelsSize));
    localStorage.setItem('periodLabelsOpacity', String(periodLabelsOpacity));
    localStorage.setItem('showPeriodSeparators', JSON.stringify(showPeriodSeparators));
    localStorage.setItem('periodColor', periodColor);
    localStorage.setItem('periodOpacity', String(periodOpacity));
    localStorage.setItem('sma10Period', String(sma10Period));
    localStorage.setItem('sma20Period', String(sma20Period));
    localStorage.setItem('sma50Period', String(sma50Period));
    localStorage.setItem('periodLineStyle', periodLineStyle);
    localStorage.setItem('periodLineWidth', String(periodLineWidth));
    localStorage.setItem('showPeriodLabels', JSON.stringify(showPeriodLabels));
    localStorage.setItem('curveTension', String(curveTension));
    localStorage.setItem('floorChartOpacity', String(floorChartOpacity));
    localStorage.setItem('sideChartOpacity', String(sideChartOpacity));
    localStorage.setItem('backChartOpacity', String(backChartOpacity));
    localStorage.setItem('floorChartLineWidth', String(floorChartLineWidth));
    localStorage.setItem('sideChartLineWidth', String(sideChartLineWidth));
    localStorage.setItem('floorSmaOpacity', String(floorSmaOpacity));
    localStorage.setItem('sideSmaOpacity', String(sideSmaOpacity));
    localStorage.setItem('backSmaOpacity', String(backSmaOpacity));
    localStorage.setItem('main3DOpacity', String(main3DOpacity));
    localStorage.setItem('sma103DOpacity', String(sma103DOpacity));
    localStorage.setItem('sma203DOpacity', String(sma203DOpacity));
    localStorage.setItem('sma503DOpacity', String(sma503DOpacity));
    localStorage.setItem('backSmaLineWidth', String(backSmaLineWidth));
    localStorage.setItem('floorSmaLineWidth', String(floorSmaLineWidth));
    localStorage.setItem('sideSmaLineWidth', String(sideSmaLineWidth));
    localStorage.setItem('backdropBlur', String(backdropBlur));
    localStorage.setItem('radialMenuColor', radialMenuColor);
    localStorage.setItem('buttonBaseColor', buttonBaseColor);

  }, [
    timeframe, controlRotateSpeed, controlPanSpeed, controlZoomSpeed, show3D, backwallStyle, showShadowFloor, showShadowSide,
    showMidLineWall, midLineWallColor, midLineWallOpacity, sideHistoryShadowStrength, historyShadowStrength, colorUp, colorDown, colorIntensity, colorContrast, momentumSensitivity,
         lineWidth3D,
    useRealData, symbol, visibleTimeframes, showSMA10, showSMA20, showSMA50,
  connectorsOpacity,
  edgesColor,
  edgesOpacity,
  buyersColor,
  sellersColor,
  floorLabelsOpacity, sideLabelsOpacity,
  titleSize,
  floorLabelsSize, sideLabelsSize, floorLabelsSpread, sideLabelsSpread,
  timeLabelsSize,
  priceLabelsSize,
  priceLabelsOpacity,
  periodLabelsSize,
  periodLabelsOpacity,
  timeLabelsOpacity,
  showPeriodSeparators, periodColor, periodOpacity, sma10Period, sma20Period, sma50Period,
  periodLineStyle, periodLineWidth, showPeriodLabels, curveTension, floorChartOpacity, sideChartOpacity, backChartOpacity, floorChartLineWidth, sideChartLineWidth, floorSmaOpacity, sideSmaOpacity, backSmaOpacity, main3DOpacity, sma103DOpacity, sma203DOpacity, sma503DOpacity, backSmaLineWidth, floorSmaLineWidth, sideSmaLineWidth, backdropBlur, radialMenuColor, buttonBaseColor,
  width3D, heightScale3D, zLimit3D,
  user
  ]);

  // All key parameters are fully saved to localStorage and can be synced manually to Firestore using the manual Save button.

  const syncWorkspaceToCloud = async () => {
    setIsSaving(true);
    setSaveMessage("");
    try {
      const config = {
        timeframe, controlRotateSpeed, controlPanSpeed, controlZoomSpeed, show3D, backwallStyle, showShadowFloor, showShadowSide,
        showMidLineWall, midLineWallColor, midLineWallOpacity, sideHistoryShadowStrength, historyShadowStrength, colorUp, colorDown, colorIntensity, colorContrast, momentumSensitivity,
        lineWidth3D, useRealData, symbol, visibleTimeframes, showSMA10, showSMA20, showSMA50,
        floorChartOpacity, sideChartOpacity, backChartOpacity, floorSmaOpacity, sideSmaOpacity, backSmaOpacity,
        main3DOpacity, sma103DOpacity, sma203DOpacity, sma503DOpacity,
        floorChartLineWidth, sideChartLineWidth, floorSmaLineWidth, sideSmaLineWidth, backSmaLineWidth,
        width3D, heightScale3D, zLimit3D, candleGraphDistance3D, floorDistance3D, sideGraphDistance3D,
        floorLabelsOpacity, sideLabelsOpacity, titleSize, floorLabelsSize, sideLabelsSize, floorLabelsSpread, sideLabelsSpread, timeLabelsSize, timeLabelsOpacity, periodLabelsSize, periodLabelsOpacity,
        edgesOpacity, backdropBlur, radialMenuColor, buttonBaseColor,
        cameraStates
      };
      const res = await fetch('/api/workspace', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: authToken, config })
      });
      if (res.ok) {
        setSaveMessage("Workspace Sincronizado na Nuvem!");
      } else {
        setSaveMessage("Erro na Sincronização.");
      }
    } catch (e) {
      setSaveMessage("Erro de Rede ao Sincronizar.");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(""), 3000);
    }
  };

  const loadWorkspaceFromCloud = async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`/api/workspace/${authToken}`);
      if (res.ok) {
        const data = await res.json();
        if (data.config) {
          const config = typeof data.config === 'string' ? JSON.parse(data.config) : data.config;
          if (config.timeframe) setTimeframe(config.timeframe);
          if (config.controlRotateSpeed !== undefined) setControlRotateSpeed(config.controlRotateSpeed);
          if (config.controlPanSpeed !== undefined) setControlPanSpeed(config.controlPanSpeed);
          if (config.controlZoomSpeed !== undefined) setControlZoomSpeed(config.controlZoomSpeed);
          if (config.show3D !== undefined) setShow3D(config.show3D);
          if (config.backwallStyle) setBackwallStyle(config.backwallStyle);
          if (config.showShadowFloor !== undefined) setShowShadowFloor(config.showShadowFloor);
          if (config.showShadowSide !== undefined) setShowShadowSide(config.showShadowSide);
          if (config.showMidLineWall !== undefined) setShowMidLineWall(config.showMidLineWall);
          if (config.midLineWallColor) setMidLineWallColor(config.midLineWallColor);
          if (config.midLineWallOpacity !== undefined) setMidLineWallOpacity(config.midLineWallOpacity);
          if (config.sideHistoryShadowStrength !== undefined) setSideHistoryShadowStrength(config.sideHistoryShadowStrength);
          if (config.historyShadowStrength !== undefined) setHistoryShadowStrength(config.historyShadowStrength);
          if (config.colorUp) setColorUp(config.colorUp);
          if (config.colorDown) setColorDown(config.colorDown);
          if (config.colorIntensity !== undefined) setColorIntensity(config.colorIntensity);
          if (config.colorContrast !== undefined) setColorContrast(config.colorContrast);
          if (config.momentumSensitivity !== undefined) setMomentumSensitivity(config.momentumSensitivity);
          if (config.lineWidth3D !== undefined) setLineWidth3D(config.lineWidth3D);
          if (config.useRealData !== undefined) setUseRealData(config.useRealData);
          if (config.symbol) setSymbol(config.symbol);
          if (config.visibleTimeframes) setVisibleTimeframes(config.visibleTimeframes);
          if (config.showSMA10 !== undefined) setShowSMA10(config.showSMA10);
          if (config.gridOpacity !== undefined) setGridOpacity(config.gridOpacity);
          if (config.gridColor !== undefined) setGridColor(config.gridColor);
          if (config.connectorsOpacity !== undefined) setConnectorsOpacity(config.connectorsOpacity);
          if (config.edgesColor !== undefined) setEdgesColor(config.edgesColor);
          if (config.buyersColor !== undefined) setBuyersColor(config.buyersColor);
          if (config.sellersColor !== undefined) setSellersColor(config.sellersColor);
          if (config.periodLineStyle !== undefined) setPeriodLineStyle(config.periodLineStyle);
          if (config.periodLineWidth !== undefined) setPeriodLineWidth(config.periodLineWidth);
          if (config.showPeriodLabels !== undefined) setShowPeriodLabels(config.showPeriodLabels);
          if (config.curveTension !== undefined) setCurveTension(config.curveTension);
          if (config.showSMA20 !== undefined) setShowSMA20(config.showSMA20);
          if (config.showSMA50 !== undefined) setShowSMA50(config.showSMA50);
          if (config.showGrid !== undefined) setShowGrid(config.showGrid);
          if (config.showConnectors !== undefined) setShowConnectors(config.showConnectors);
          if (config.connectorsOpacity !== undefined) setConnectorsOpacity(config.connectorsOpacity);
          if (config.gridColor !== undefined) setGridColor(config.gridColor);
          if (config.gridOpacity !== undefined) setGridOpacity(config.gridOpacity);
          if (config.edgesColor !== undefined) setEdgesColor(config.edgesColor);
          if (config.edgesOpacity !== undefined) setEdgesOpacity(config.edgesOpacity);
          if (config.buyersColor !== undefined) setBuyersColor(config.buyersColor);
          if (config.sellersColor !== undefined) setSellersColor(config.sellersColor);
          if (config.floorLabelsOpacity !== undefined) setFloorLabelsOpacity(config.floorLabelsOpacity);
          if (config.sideLabelsOpacity !== undefined) setSideLabelsOpacity(config.sideLabelsOpacity);
          if (config.titleSize !== undefined) setTitleSize(config.titleSize);
          if (config.floorLabelsSize !== undefined) setFloorLabelsSize(config.floorLabelsSize);
          if (config.sideLabelsSize !== undefined) setSideLabelsSize(config.sideLabelsSize);
          if (config.floorLabelsSpread !== undefined) setFloorLabelsSpread(config.floorLabelsSpread);
          if (config.sideLabelsSpread !== undefined) setSideLabelsSpread(config.sideLabelsSpread);
          if (config.timeLabelsSize !== undefined) setTimeLabelsSize(config.timeLabelsSize);
          if (config.timeLabelsOpacity !== undefined) setTimeLabelsOpacity(config.timeLabelsOpacity);
          if (config.momentumLabelsSize !== undefined) setMomentumLabelsSize(config.momentumLabelsSize);
          if (config.momentumLabelsOpacity !== undefined) setMomentumLabelsOpacity(config.momentumLabelsOpacity);
          if (config.priceLabelsSize !== undefined) setPriceLabelsSize(config.priceLabelsSize);
          if (config.priceLabelsOpacity !== undefined) setPriceLabelsOpacity(config.priceLabelsOpacity);
          if (config.showPeriodSeparators !== undefined) setShowPeriodSeparators(config.showPeriodSeparators);
          if (config.periodColor !== undefined) setPeriodColor(config.periodColor);
          if (config.periodOpacity !== undefined) setPeriodOpacity(config.periodOpacity);
          if (config.sma10Period !== undefined) setSma10Period(config.sma10Period);
          if (config.sma20Period !== undefined) setSma20Period(config.sma20Period);
          if (config.sma50Period !== undefined) setSma50Period(config.sma50Period);
          if (config.width3D !== undefined) {
            setWidth3D(config.width3D);
            localStorage.setItem('width3D', String(config.width3D));
          }
          if (config.heightScale3D !== undefined) {
            setHeightScale3D(config.heightScale3D);
            localStorage.setItem('heightScale3D', String(config.heightScale3D));
          }
          if (config.zLimit3D !== undefined) {
            setZLimit3D(config.zLimit3D);
            localStorage.setItem('zLimit3D', String(config.zLimit3D));
          }
          if (config.lockedSliders !== undefined) {
            setLockedSliders(config.lockedSliders);
            localStorage.setItem('lockedSliders', JSON.stringify(config.lockedSliders));
          }
          if (config.connectorsThickness !== undefined) {
            setConnectorsThickness(config.connectorsThickness);
            localStorage.setItem('connectorsThickness', String(config.connectorsThickness));
          }
          if (config.cameraStates) {
            setCameraStates(config.cameraStates);
            localStorage.setItem('cameraStates', JSON.stringify(config.cameraStates));
          }
          setSaveMessage("Workspace Carregado com Sucesso.");
          setTimeout(() => setSaveMessage(""), 3000);
        }
      }
    } catch (e) {
      console.warn("Could not load cloud workspace", e);
    }
  };

  useEffect(() => {
    // Removed automatic loadWorkspaceFromCloud() to prevent stale Cloud SQL 
    // snapshots from overwriting real-time Firestore sync (useFirebaseSync).
  }, [authStatus]); // trigger when authStatus changes to authenticated

  const saveSettings = async () => {
    if (!user) return;
    setIsSaving(true);
    setSaveMessage("");
    const docPath = `users/${user.uid}`;
    try {
      const docRef = doc(db, 'users', user.uid);
      await setDoc(docRef, {
        timeframe,
        controlRotateSpeed,
        controlPanSpeed,
        controlZoomSpeed,
        show3D,
        backwallStyle,
        showShadowFloor,
        showShadowSide,
        showMidLineWall,
        midLineWallColor,
        midLineWallOpacity,
        sideHistoryShadowStrength,
        historyShadowStrength,
        colorUp,
        colorDown,
        colorIntensity,
        colorContrast,
        momentumSensitivity,
        
        lineWidth3D,
        useRealData,
        symbol,
        visibleTimeframes,
        showSMA10,
        showSMA20,
        showSMA50,
        showGrid,
  gridColor,
  gridOpacity,
        showConnectors,
  connectorsOpacity,
  edgesColor,
  edgesOpacity,
  buyersColor,
  sellersColor,
  floorLabelsOpacity, sideLabelsOpacity,
  titleSize,
  floorLabelsSize, sideLabelsSize, floorLabelsSpread, sideLabelsSpread,
  timeLabelsSize,
  periodLabelsSize,
  periodLabelsOpacity,
  timeLabelsOpacity,
  showPeriodSeparators, periodColor, periodOpacity,
        sma10Period,
        sma20Period,
        sma50Period,
        floorChartOpacity, sideChartOpacity, backChartOpacity, floorSmaOpacity, sideSmaOpacity, backSmaOpacity,
        main3DOpacity, sma103DOpacity, sma203DOpacity, sma503DOpacity,
        floorChartLineWidth, sideChartLineWidth, floorSmaLineWidth, sideSmaLineWidth, backSmaLineWidth,
        candleGraphDistance3D,
        floorDistance3D,
        sideGraphDistance3D,
        width3D,
        heightScale3D,
        zLimit3D,
        lockedSliders,
        connectorsThickness,
        cameraStates
      }, { merge: true });
      setSaveMessage("Configurações salvas!");
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, docPath);
      setSaveMessage("Erro ao salvar.");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(""), 3000);
    }
  };

  const resetSettings = () => {
    let defaults: any = adminDefaultsRef.current;
    if (!defaults) {
      const saved = localStorage.getItem('flowaes_admin_global_defaults');
      if (saved) {
        try { defaults = JSON.parse(saved); } catch (e) { defaults = null; }
      }
    }

    if (defaults) {
      if (defaults.controlRotateSpeed !== undefined) setControlRotateSpeed(defaults.controlRotateSpeed);
      if (defaults.controlPanSpeed !== undefined) setControlPanSpeed(defaults.controlPanSpeed);
      if (defaults.controlZoomSpeed !== undefined) setControlZoomSpeed(defaults.controlZoomSpeed);
      if (defaults.show3D !== undefined) setShow3D(defaults.show3D);
      if (defaults.backwallStyle) setBackwallStyle(defaults.backwallStyle);
      if (defaults.showShadowFloor !== undefined) setShowShadowFloor(defaults.showShadowFloor);
      if (defaults.showShadowSide !== undefined) setShowShadowSide(defaults.showShadowSide);
      if (defaults.showMidLineWall !== undefined) setShowMidLineWall(defaults.showMidLineWall);
      if (defaults.midLineWallColor) setMidLineWallColor(defaults.midLineWallColor);
      if (defaults.midLineWallOpacity !== undefined) setMidLineWallOpacity(defaults.midLineWallOpacity);
      if (defaults.sideHistoryShadowStrength !== undefined) setSideHistoryShadowStrength(defaults.sideHistoryShadowStrength);
      if (defaults.historyShadowStrength !== undefined) setHistoryShadowStrength(defaults.historyShadowStrength);
      if (defaults.colorUp) setColorUp(defaults.colorUp);
      if (defaults.colorDown) setColorDown(defaults.colorDown);
      if (defaults.colorIntensity !== undefined) setColorIntensity(defaults.colorIntensity);
      if (defaults.colorContrast !== undefined) setColorContrast(defaults.colorContrast);
      if (defaults.momentumSensitivity !== undefined) setMomentumSensitivity(defaults.momentumSensitivity);
      if (defaults.lineWidth3D !== undefined) setLineWidth3D(defaults.lineWidth3D);
      if (defaults.useRealData !== undefined) setUseRealData(defaults.useRealData);
      if (defaults.showSMA10 !== undefined) setShowSMA10(defaults.showSMA10);
      if (defaults.showSMA20 !== undefined) setShowSMA20(defaults.showSMA20);
      if (defaults.showSMA50 !== undefined) setShowSMA50(defaults.showSMA50);
      if (defaults.showGrid !== undefined) setShowGrid(defaults.showGrid);
      if (defaults.gridColor) setGridColor(defaults.gridColor);
      if (defaults.gridOpacity !== undefined) setGridOpacity(defaults.gridOpacity);
      if (defaults.gridThickness !== undefined) setGridThickness(defaults.gridThickness);
      if (defaults.showConnectors !== undefined) setShowConnectors(defaults.showConnectors);
      if (defaults.connectorsOpacity !== undefined) setConnectorsOpacity(defaults.connectorsOpacity);
      if (defaults.connectorsThickness !== undefined) setConnectorsThickness(defaults.connectorsThickness);
      if (defaults.edgesColor) setEdgesColor(defaults.edgesColor);
      if (defaults.edgesOpacity !== undefined) setEdgesOpacity(defaults.edgesOpacity);
      if (defaults.buyersColor) setBuyersColor(defaults.buyersColor);
      if (defaults.sellersColor) setSellersColor(defaults.sellersColor);
      if (defaults.floorLabelsOpacity !== undefined) setFloorLabelsOpacity(defaults.floorLabelsOpacity);
      if (defaults.sideLabelsOpacity !== undefined) setSideLabelsOpacity(defaults.sideLabelsOpacity);
      if (defaults.titleSize !== undefined) setTitleSize(defaults.titleSize);
      if (defaults.floorLabelsSize !== undefined) setFloorLabelsSize(defaults.floorLabelsSize);
      if (defaults.sideLabelsSize !== undefined) setSideLabelsSize(defaults.sideLabelsSize);
      if (defaults.floorLabelsSpread !== undefined) setFloorLabelsSpread(defaults.floorLabelsSpread);
      if (defaults.sideLabelsSpread !== undefined) setSideLabelsSpread(defaults.sideLabelsSpread);
      if (defaults.timeLabelsSize !== undefined) setTimeLabelsSize(defaults.timeLabelsSize);
      if (defaults.timeLabelsOpacity !== undefined) setTimeLabelsOpacity(defaults.timeLabelsOpacity);
      if (defaults.momentumLabelsSize !== undefined) setMomentumLabelsSize(defaults.momentumLabelsSize);
      if (defaults.momentumLabelsOpacity !== undefined) setMomentumLabelsOpacity(defaults.momentumLabelsOpacity);
      if (defaults.priceLabelsSize !== undefined) setPriceLabelsSize(defaults.priceLabelsSize);
      if (defaults.priceLabelsOpacity !== undefined) setPriceLabelsOpacity(defaults.priceLabelsOpacity);
      if (defaults.periodLabelsSize !== undefined) setPeriodLabelsSize(defaults.periodLabelsSize);
      if (defaults.periodLabelsOpacity !== undefined) setPeriodLabelsOpacity(defaults.periodLabelsOpacity);
      if (defaults.showPeriodSeparators !== undefined) setShowPeriodSeparators(defaults.showPeriodSeparators);
      if (defaults.periodColor) setPeriodColor(defaults.periodColor);
      if (defaults.periodOpacity !== undefined) setPeriodOpacity(defaults.periodOpacity);
      if (defaults.sma10Period !== undefined) setSma10Period(defaults.sma10Period);
      if (defaults.sma20Period !== undefined) setSma20Period(defaults.sma20Period);
      if (defaults.sma50Period !== undefined) setSma50Period(defaults.sma50Period);
      if (defaults.periodLineStyle) setPeriodLineStyle(defaults.periodLineStyle);
      if (defaults.periodLineWidth !== undefined) setPeriodLineWidth(defaults.periodLineWidth);
      if (defaults.showPeriodLabels !== undefined) setShowPeriodLabels(defaults.showPeriodLabels);
      if (defaults.curveTension !== undefined) setCurveTension(defaults.curveTension);
      if (defaults.floorChartOpacity !== undefined) setFloorChartOpacity(defaults.floorChartOpacity);
      if (defaults.sideChartOpacity !== undefined) setSideChartOpacity(defaults.sideChartOpacity);
      if (defaults.backChartOpacity !== undefined) setBackChartOpacity(defaults.backChartOpacity);
      if (defaults.floorSmaOpacity !== undefined) setFloorSmaOpacity(defaults.floorSmaOpacity);
      if (defaults.sideSmaOpacity !== undefined) setSideSmaOpacity(defaults.sideSmaOpacity);
      if (defaults.backSmaOpacity !== undefined) setBackSmaOpacity(defaults.backSmaOpacity);
      if (defaults.main3DOpacity !== undefined) setMain3DOpacity(defaults.main3DOpacity);
      if (defaults.sma103DOpacity !== undefined) setSma103DOpacity(defaults.sma103DOpacity);
      if (defaults.sma203DOpacity !== undefined) setSma203DOpacity(defaults.sma203DOpacity);
      if (defaults.sma503DOpacity !== undefined) setSma503DOpacity(defaults.sma503DOpacity);
      if (defaults.candleGraphDistance3D !== undefined) setCandleGraphDistance3D(defaults.candleGraphDistance3D);
      if (defaults.floorDistance3D !== undefined) setFloorDistance3D(defaults.floorDistance3D);
      if (defaults.sideGraphDistance3D !== undefined) setSideGraphDistance3D(defaults.sideGraphDistance3D);
      if (defaults.backdropBlur !== undefined) setBackdropBlur(defaults.backdropBlur);
      if (defaults.radialMenuColor) setRadialMenuColor(defaults.radialMenuColor);
      if (defaults.buttonBaseColor) setButtonBaseColor(defaults.buttonBaseColor);
      setSaveMessage("Padrões Globais do ADM Restaurados!");
    } else {
      setShow3D(true);
      setBackwallStyle('candles');
      setShowShadowFloor(true);
      setShowShadowSide(true);
      setColorUp('#00ffa3');
      setColorDown('#ff3d67');
      setColorIntensity(1.5);
      setColorContrast(1.1);
      setMomentumSensitivity(2.5);
      setCandleGraphDistance3D(0.35);
      setFloorDistance3D(0.25);
      setSideGraphDistance3D(0.40);
      setUseRealData(false);
      setShowSMA10(true);
      setShowSMA20(false);
      setShowSMA50(false);
      setSma10Period(10);
      setSma20Period(20);
      setSma50Period(50);
      setFloorChartOpacity(0.15);
      setSideChartOpacity(0.10);
      setBackChartOpacity(0.15);
      setSideHistoryShadowStrength(0.85);
      setHistoryShadowStrength(0.85);
      setFloorSmaOpacity(0.15);
      setSideSmaOpacity(0.10);
      setBackSmaOpacity(0.15);
      setPeriodOpacity(0.4);
      setInteractionMode('orbit');
      setSaveMessage("Padrões restaurados!");
    }
    setTimeout(() => setSaveMessage(""), 3000);
  };
  
  resetSettingsRef.current = resetSettings;

/* removed duplicated state */


  /* removed duplicated state */
  const generator = useMemo(() => new WaveGenerator(), []);
  
  const generatedData = useMemo(() => {
    return generator.generate(timeframe);
  }, [timeframe, generator]);
  // F1 C2: o estado da navegação (alvo, posição, limite) fica em ÍNDICE ANCORADO (src/utils/anchoredNav.ts).
  // targetHistoryOffset e historyOffsetRef são a FRONTEIRA: vistas em distância do fim, que é o que a cena, o
  // fatiador, o autoplay e as sondas ainda usam (até o F2). Lêem e escrevem o estado ancorado sem arredondamento.
  const navRef = useRef<AnchoredNav | null>(null);
  if (navRef.current === null) { navRef.current = new AnchoredNav(); if (navRec) navRec.instrument(navRef.current); } // P1b: só anota as escritas (?dbg=1)
  // F2: velas novas ao vivo que já entraram nas velas brutas e ainda não estão numa série processada commitada.
  // O índice ancorado da vela mais nova só anda quando a série processada que as contém é entregue (commitProcessed),
  // para a navegação e os dados da cena falarem sempre da mesma vela mais nova.
  const pendingLiveRef = useRef(0);
  // F3a.1: conta as trocas de série (ativo/timeframe). A navegação é re-ancorada NA HORA da troca, mas a janela nova só
  // existe quando a carga inicial chega (seriesRef ainda é a antiga nesse meio-tempo): uma carga da série antiga que
  // terminasse aí passava pela guarda `seriesRef.current !== win` e deslocava o alvo da série nova (replay "presente":
  // alvo 0 → 2 → 0 = RECUO na volta para 1h).
  const seriesEpochRef = useRef(0);
  // F3b C2: troca de série no MESMO momento. `T` = horário da vela do centro da vista antes da troca (null = presente);
  // a série nova é carregada em volta dele e a vista nasce com essa vela no centro. `switching` = a série anterior
  // ainda está na tela (aviso "carregando", sem escurecer); `retrySwitchRef` = "tentar de novo" da carga em volta de T.
  const switchRef = useRef<{ T: number | null; epoch: number; dir?: Direction } | null>(null);
  // F3c C4: troca em volta de T: a pré-carga espera o primeiro quadro desenhado da série nova (não disputa a primeira
  // vista) e começa pelo lado para onde a vista andava antes da troca
  const preloadHoldRef = useRef(false);
  const [preloadGate, setPreloadGate] = useState(0);
  /** ?dbg=1 (F3c C1): ativo e timeframe da série na tela, para o "de → para" das trocas */
  const seriesLabelRef = useRef('');
  /** ativo da série na tela (a troca de ativo abre no presente) */
  const seriesSymbolRef = useRef<string | null>(null);
  const [switching, setSwitching] = useState(false);
  /** F3d C3: progresso da troca no aviso (pedidos com resposta / total) */
  const [switchProgress, setSwitchProgress] = useState('');
  const retrySwitchRef = useRef<(() => void) | null>(null);
  /**
   * Entrega uma série processada: aplica as velas novas pendentes ao estado ancorado e etiqueta a série.
   * F3a: `snapNewestAbs` = índice ancorado da vela mais nova da janela no instante do recorte processado; cargas e
   * despejos que mudaram a vela mais nova mantêm alvo e posição nas mesmas velas (AnchoredNav.dataShifted).
   */
  const commitProcessed = (processed: any[], snapNewestAbs?: number) => {
    const tDeliverP1 = p1T(); // P1 (?dbg=1): etapa "entrega dos dados"
    const nav = navRef.current!, k = pendingLiveRef.current;
    pendingLiveRef.current = 0;
    if (k > 0) nav.liveAppended(k);
    if (snapNewestAbs !== undefined) nav.dataShifted(snapNewestAbs);
    // "volta ao presente" com a janela fora do presente: o alvo acompanha o fim novo dos dados até chegar ao presente
    if (isReturningToLiveRef.current && nav.targetDist !== 0) { navSource(SRC.AUTOPLAY); nav.target.current = 0; navSource(0); }
    if (DBG_ENABLED && seriesRef.current) dbgWindowState(windowLimitRef.current, seriesRef.current.length, windowAtPresentRef.current, tailRef.current.length, knownStartRef.current);
    setRealData(tagSeries(tagNewestAbs(processed, nav.newestGlobal), seriesEpochRef.current));
    p1End(P1S.DELIV, tDeliverP1, true);
    if (swProbe) swProbe.commit(performance.now(), processed, knownStartRef.current !== null, windowAtPresentRef.current, !!navRec && navRec.src === SRC.VIVO); // F3c C1 / F3d C5 (?dbg=1): vela ao vivo à parte
  };
  /**
   * F3b C2: primeira série processada depois de uma troca de timeframe/ativo. Põe a vela que contém T (o centro da
   * vista antes da troca) no centro, com encosto no fim/presente e no início, ANTES do commit: o primeiro quadro da
   * série nova já mostra o momento certo (sem abrir no presente e sem "voltar sozinho"). Sem T: presente.
   */
  const placeAtSwitch = (processed: any[], ivMs: number) => {
    const sw = switchRef.current, nav = navRef.current!;
    switchRef.current = null;
    p1SeriesSwitch(false);
    setSwitching(false); setSwitchProgress('');
    histRuntime.switching = false;
    let d = 0;
    if (sw && sw.T !== null) {
      const raw = seriesRef.current?.length ?? processed.length;
      const total = Math.min(processed.length, raw > MATH_WARMUP_DROP * 2 ? raw - MATH_WARMUP_DROP : raw);
      const maxD = Math.max(0, total - NAV_VISIBLE_CANDLES);
      const c = centroParaMomento(sw.T, processed, ivMs);
      d = Math.min(maxD, c.kind === 'ok' ? c.dist : c.kind === 'antesDaSerie' ? maxD : 0);
      // F3c C1 (?dbg=1): Δ = velas entre o centro posto e a vela que contém T (≠ 0 só com encosto)
      if (swProbe) {
        swProbe.vista(performance.now(), processed, d, ivMs, c.kind === 'ok' ? d - c.dist : NaN, c.kind === 'ok' && c.encosto ? c.encosto : c.kind === 'antesDaSerie' ? 'início' : '', knownStartRef.current !== null, windowAtPresentRef.current);
        requestAnimationFrame(() => swProbe!.quadro(performance.now()));
      }
      if (DBG_ENABLED) dbgEvent(`troca de série: vela de ${new Date(sw.T).toISOString().slice(0, 16)} no centro → distância ${d}${c.kind === 'ok' && c.encosto ? ` (encosto: ${c.encosto})` : c.kind === 'antesDaSerie' ? ' (encosto: início)' : ''}`);
    }
    if (swProbe && !(sw && sw.T !== null)) { swProbe.vista(performance.now(), processed, 0, ivMs, 0, '', knownStartRef.current !== null, windowAtPresentRef.current); requestAnimationFrame(() => swProbe!.quadro(performance.now())); }
    navSource(SRC.TROCA); placeNavAt(d); navSource(0);
    if (sw && sw.T !== null) {
      // F3d: a janela final já tem a folga da pré-carga do lado da direção (sem direção: o passado); a mesma direção
      // aqui, senão a pré-carga "parada" pediria os dois lados e a janela cheia iria e voltaria
      moveDirRef.current = sw.dir === 1 ? 1 : -1;
      requestAnimationFrame(() => { if (switchRef.current === null && preloadHoldRef.current) { preloadHoldRef.current = false; setPreloadGate(x => x + 1); } });
    }
  };
  /** Alvo e posição em `d` (distância do fim) num salto programado: sem "movimento" nem RECUO (bases do laço, da
   * direção e da chave React acertadas junto). */
  const placeNavAt = (d: number) => {
    const nav = navRef.current!;
    nav.target.current = d; nav.position.current = d;
    const h = historyOffsetRef as any;
    h.lastTarget = d; h.lastTargetIdx = nav.targetIndex; h.lastFrameValue = d;
    const key = nav.keyOf(d, nav.newestGlobal); h.lastStateKey = key; setHistoryKey(key);
    lastMotionRef.current = motionOf(historyOffsetRef); moveDirRef.current = 0;
  };
  // F3b C5: volta ao presente INSTANTÂNEA (sem autoplay). Para o "Ao vivo" do long press (bloco de usabilidade; a UI
  // não existe ainda). Janela com o presente: a vista vai ao fim na hora. Janela fora do presente: a mesma série é
  // recarregada no presente pelo caminho da troca (bloco final com as velas ao vivo; o gráfico atual fica na tela com
  // "carregando" até o bloco chegar e então a vista aparece no presente, sem quadro intermediário).
  const [presentNonce, setPresentNonce] = useState(0);
  const forcePresentRef = useRef(false);
  const irParaTempo = (T: 'agora') => {
    if (T !== 'agora') return;
    histEvent('x');
    // com troca pendente a janela na tela é a da série anterior: recarrega já no presente (a carga em volta de T é
    // descartada pela guarda de série)
    if (!useRealData || (!switchRef.current && (!seriesRef.current || windowAtPresentRef.current))) { navSource(SRC.IR_TEMPO); placeNavAt(0); navSource(0); return; }
    forcePresentRef.current = true;
    setPresentNonce(x => x + 1);
  };
  // F2 T2: o estado React da navegação guarda uma CHAVE estável (AnchoredNav.keyOf): com a política "fica nas mesmas
  // velas" ela não muda quando chega vela nova ao vivo (sem render extra do App); com "acompanha o presente" a chave
  // é a própria distância do fim (idêntico ao de antes). `historyOffset` é derivado, em distância do fim RELATIVA à
  // série processada deste render; `setHistoryOffset` continua recebendo distância do fim (relativa ao presente).
  const historyOffset = navRef.current.distOf(historyKey, useRealData ? newestAbsOf(realData) : NaN);
  const setHistoryOffset = useCallback((v: React.SetStateAction<number>) => setHistoryKey(prevKey => {
    const nav = navRef.current!, g = nav.newestGlobal;
    return nav.keyOf(typeof v === 'function' ? v(nav.distOf(prevKey, g)) : v, g);
  }), []);
  const targetHistoryOffset = navRef.current.target;
  const timeDragPointerXRef = useRef<number | null>(null);
  
  const timeSensitivityRef = useRef(3);
  useEffect(() => {
    timeSensitivityRef.current = timeSensitivity;
  }, [timeSensitivity]);

  const returnSensitivityRef = useRef(3);
  useEffect(() => {
    returnSensitivityRef.current = returnSensitivity;
  }, [returnSensitivity]);

  const isReturningToLiveRef = useRef(false);
  useEffect(() => {
    isReturningToLiveRef.current = isReturningToLive;
    if (!isReturningToLive) dbgAutoplayEnd(); // diagnóstico (?dbg=1): fim do autoplay (chegou ao presente ou foi cancelado)
  }, [isReturningToLive]);

  // F3b C3: máquina de estados do modo histórico (utils/historyMode.ts). Abrir a roleta, começar uma troca de série e o
  // X passam por aqui: o próximo estado vem de histStep e é copiado para os estados reais (modo, autoplay, gesto).
  // Sair do autoplay congela o alvo NA POSIÇÃO ATUAL (estado ancorado, não o estado React atrasado): o gráfico fica
  // onde está.
  const interactionModeRef = useRef(interactionMode);
  interactionModeRef.current = interactionMode;
  const radialOpenRef = useRef(false);
  const histEvent = (ev: HistEvent): HistState => {
    const cur: HistState = {
      mode: interactionModeRef.current as HistMode, returning: isReturningToLiveRef.current,
      dragging: useFlowaesStore.getState().activeGesture === 'time_drag', roulette: radialOpenRef.current, switching: histRuntime.switching,
    };
    const next = histStep(cur, ev);
    if (cur.returning && !next.returning) {
      const nav = navRef.current!;
      navSource(SRC.X_ROLETA); nav.target.current = nav.position.current; navSource(0);
      isReturningToLiveRef.current = false;
      setIsReturningToLive(false);
    }
    if (next.mode !== cur.mode) { interactionModeRef.current = next.mode; setInteractionMode(next.mode); }
    if (cur.dragging && !next.dragging) setActiveGesture(null);
    if (DBG_ENABLED && histName(cur) !== histName(next)) dbgEvent(`modo: ${histName(cur)} → ${histName(next)} (${ev})`);
    if (DBG_ENABLED) { const v = histViolations(next); if (v.length) dbgEvent(`ESTADO INVÁLIDO: ${v.join(', ')}`); }
    return next;
  };
  const openRadialMenu = (pos: { x: number; y: number }) => {
    histEvent('abrirRoleta');
    radialOpenRef.current = true;
    setRadialMenuPos(pos);
  };
  useEffect(() => { radialOpenRef.current = radialMenuPos !== null; }, [radialMenuPos]);

  useEffect(() => {
    if (historyOffset === 0 && isReturningToLive && windowAtPresent) {
      setIsReturningToLive(false);
      setTimeSensitivity(3);
      setReturnSensitivity(3);
    }
  }, [historyOffset, isReturningToLive, windowAtPresent]);

  // Otimização: Isolar loop de animação da renderização global do React
  const historyOffsetRef = navRef.current.position;
  useEffect(() => {
    let rafId: number;
    let lastUpdateTime = performance.now();
    const smoothLoop = () => {
      const now = performance.now();
      const deltaSec = Math.min(0.1, Math.max(0.001, (now - lastUpdateTime) / 1000));
      lastUpdateTime = now;

      const prev = historyOffsetRef.current;
      const target = targetHistoryOffset.current;
      const isDragging = timeDragPointerXRef.current !== null;
      const isReturning = isReturningToLiveRef.current;
      
      let nextOffset = prev;
      
      if (isReturning) {
          const diff = target - prev;
          if (Math.abs(diff) >= 0.001) {
             const sensitivity = returnSensitivityRef.current;
             // Velocidade linear constante e sem rampa: 15 / 60 / 180 candles por segundo
             const candlesPerSec = sensitivity === 3 ? 180 : sensitivity === 2 ? 60 : 15;
             const maxStep = candlesPerSec * deltaSec;
             const step = Math.sign(diff) * Math.min(Math.abs(diff), maxStep);
             nextOffset = Math.max(0, prev + step);
          } else {
             nextOffset = Math.max(0, target);
          }
      } else {
          nextOffset = Math.max(0, target);
      }
      
      if (Math.abs(nextOffset - prev) >= 0.001) {
         if (navRec) navSource(isReturning ? SRC.AUTOPLAY : SRC.LACO);
         historyOffsetRef.current = nextOffset;
         if (navRec) navSource(0);
      }
      
      // Diagnóstico (?dbg=1): alvo diminuiu sem ser pedido = recuo. Fora do autoplay INTEIRO (o alvo vai a 0 por
      // projeto; a marca vale do clique até o fim do retorno) e fora do próprio arrasto para o presente (o último
      // movimento grava o alvo e o gesto é solto antes deste quadro). Recuos pelo limite continuam avisados.
      // F3a.1: comparado em ÍNDICE ANCORADO (a vela do alvo), não em distância do fim: carga, despejo e costura mudam a
      // distância sem mover a vista (despejo das 1.000 mais novas = "alvo 6746 → 5755" falso). Seguir o ao vivo no
      // presente (distância < 1 antes e depois) também não é recuo.
      const navD = navRef.current!, tIdx = navD.targetIndex;
      if (DBG_ENABLED && !isReturning && !dbgState.autoplayActive && now - dbgState.autoplayStartT > 1000 && useFlowaesStore.getState().activeGesture !== 'time_drag') {
        const lastI = (historyOffsetRef as any).lastTargetIdx, lastT = (historyOffsetRef as any).lastTarget;
        if (lastI !== undefined && !(lastT < 1 && target < 1) && isRealRecuo(lastI, tIdx, (historyOffsetRef as any).lastLoopT ?? -Infinity)) { dbgEvent(`RECUO: alvo ${lastT.toFixed(0)} → ${target.toFixed(0)} (${(lastI - tIdx).toFixed(0)} velas) sem arrasto`); dbgWindowRecuo(lastI, tIdx); }
      }
      (historyOffsetRef as any).lastTargetIdx = tIdx;
      (historyOffsetRef as any).lastLoopT = now;
      const isTargetChanging = Math.abs(targetHistoryOffset.current - ((historyOffsetRef as any).lastTarget || 0)) > 0.001;
      (historyOffsetRef as any).lastTarget = targetHistoryOffset.current;
      
      const isMoving = Math.abs(targetHistoryOffset.current - historyOffsetRef.current) >= 0.1;
      
      const lastReactUpdate = (historyOffsetRef as any).lastReactUpdate || 0;
      const isSettled = !isDragging && !isTargetChanging && !isMoving;
      
      // Heartbeat State Update: update React when completely settled, OR every 50ms (20 FPS) while moving/dragging
      if (isSettled || (now - lastReactUpdate > 50)) {
         const valueToSet = Math.max(0, isSettled ? targetHistoryOffset.current : historyOffsetRef.current);
         // check against previous state to avoid infinite loops (F2: compara CHAVES; vela nova ao vivo não muda a chave)
         const nav = navRef.current!, key = nav.keyOf(valueToSet, nav.newestGlobal);
         const lastKey = (historyOffsetRef as any).lastStateKey ?? INITIAL_HISTORY_KEY;
         if (lastKey !== key && Math.abs(lastKey - key) > 0.1) {
             (historyOffsetRef as any).lastStateKey = key;
             (historyOffsetRef as any).lastReactUpdate = now;
             setHistoryKey(key);
         }
      }
      
      rafId = requestAnimationFrame(smoothLoop);

    };
    rafId = requestAnimationFrame(smoothLoop);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const isLoadingHistoryRef = useRef<boolean>(false);

  // true enquanto a carga em andamento é pré-carga (em segundo plano): o aviso "Sincronizando..." não aparece
  const loadBackgroundRef = useRef(false);
  // F1 C3: as cargas de histórico passam pelo CandleSource (resultado tipado). FALHA NÃO É FIM: só "vazio" ou
  // "fimDosDados" marcam o fim do histórico. Uma falha (rede, limite 429, bloqueio 451, fonte trocada) mostra um aviso
  // com "tentar de novo" e tenta sozinha de novo com recuo progressivo (src/utils/loadRetry.ts).
  const candleSourceRef = useRef(new BinanceCandleSource());
  // F3e C4: `background` = pré-carga/carga de borda automática (aviso só na 3ª falha perto da borda); `lado` = de que
  // borda era a carga (a distância da vista a ela decide o aviso)
  type LoadFailure = { tipo: CandleErrorKind; status: number | null; attempt: number; retryAt: number; background: boolean; lado: 'older' | 'newer' | 'troca' };
  const loadFailRef = useRef<LoadFailure | null>(null);
  const [loadFailure, setLoadFailure] = useState<LoadFailure | null>(null);
  const [retryTick, setRetryTick] = useState(0);            // re-dispara os efeitos de carga quando o recuo acaba
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearLoadFailure = () => {
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
    if (loadFailRef.current) { loadFailRef.current = null; setLoadFailure(null); }
  };
  /** Ainda no recuo depois de uma falha (as cargas automáticas esperam; "tentar de novo" não espera). */
  const inRetryBackoff = () => !!loadFailRef.current && performance.now() < loadFailRef.current.retryAt;
  /** Falha de carga (rede, limite, bloqueio, fonte trocada): falha ≠ fim; aviso visível e nova tentativa com recuo. */
  const failLoad = (tipo: CandleErrorKind, status: number | null, message: string, background: boolean, what: string) => {
    const attempt = (loadFailRef.current?.attempt ?? 0) + 1;
    const delay = nextRetryDelayMs(attempt);
    const fail: LoadFailure = { tipo, status, attempt, retryAt: performance.now() + delay, background, lado: what.startsWith('troca') ? 'troca' : what.includes('novas') ? 'newer' : 'older' };
    loadFailRef.current = fail; setLoadFailure(fail);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => { retryTimerRef.current = null; setRetryTick(x => x + 1); }, delay);
    console.warn(`[HistoryLoad] falha (${tipo}${status ? ' ' + status : ''}): ${message}`);
    if (DBG_ENABLED) {
      dbgLoadDone({ ended: false, failed: tipo + (status ? ' ' + status : '') });
      dbgEvent(`${what}: FALHA (${tipo}${status ? ' ' + status : ''}${background ? ', pré-carga' : ''}) — tentativa ${attempt}, nova tentativa em ${(delay / 1000).toFixed(0)}s; fim do histórico NÃO marcado`);
    }
  };

  /**
   * F3a C2: depois de uma carga ou de uma vela nova, se a janela passou do limite, despeja blocos inteiros do lado
   * MAIS DISTANTE da vista (windowPolicy.evictionPlan), nunca a vista nem o alvo. Despejar o lado do presente tira o
   * presente da janela (as velas novas ao vivo deixam de entrar nela). Devolve quantas velas saíram.
   */
  const trimWindow = (win: SeriesWindow, keepSide: 'oldest' | 'newest' | null = null): number => {
    const nav = navRef.current!;
    const posL = win.absToLocal(nav.posAbs), tgtL = win.absToLocal(nav.targetAbs);
    const plan = evictionPlan(win.length, windowLimitRef.current, Math.min(posL, tgtL) - NAV_VISIBLE_CANDLES, Math.max(posL, tgtL), keepSide);
    if (!plan.side) return 0;
    if (plan.side === 'oldest') {
      win.evictOldest(plan.count);
      // o início real dos dados saiu da janela: a borda mais antiga volta a carregar (sem pedido, se já conhecida)
      if (knownStartRef.current !== null && win.oldestTime > knownStartRef.current) setHasReachedHistoryEnd(false);
    } else {
      win.evictNewest(plan.count);
      if (windowAtPresentRef.current) tailRef.current = [];   // a cauda começa vazia: as próximas velas ao vivo
      setWindowAtPresent(false);
    }
    if (DBG_ENABLED) dbgWindowEvict(plan.side, plan.count);
    if (DBG_ENABLED) dbgEvent(`janela: despejo de ${plan.count} velas ${plan.side === 'oldest' ? 'mais antigas' : 'mais novas (presente fora da janela)'} → ${win.length} velas`);
    return plan.count;
  };

  const loadMoreHistory = async (background: boolean = false, manual: boolean = false) => {
    const win = seriesRef.current;
    if (!symbol || !win || win.length === 0 || hasReachedHistoryEnd || isLoadingHistoryRef.current) return;
    // F3b: com troca (ou recarga) pendente, a janela na tela é a da série anterior: nada de carga nela (uma resposta
    // em cache chegava na hora e era entregue com a etiqueta da série nova)
    if (switchRef.current) return;
    if (!manual && inRetryBackoff()) return;
    // F3a: o início real dos dados já é conhecido e está na janela: é o fim, sem pedido
    if (knownStartRef.current !== null && win.oldestTime <= knownStartRef.current) { setHasReachedHistoryEnd(true); return; }

    isLoadingHistoryRef.current = true;
    loadBackgroundRef.current = background;
    setIsLoadingHistory(true);
    const tStart = performance.now();
    if (DBG_ENABLED) dbgLoadStart();
    if (DBG_ENABLED && background) dbgEvent(`pré-carga: início (carregadas ${win.length} velas, posição ${historyOffsetRef.current.toFixed(0)})`);
    if (swProbe) swProbe.precarga(performance.now());
    try {
      const endTime = win.oldestTime; // timestamp of the oldest candle
      
      const interval = activeIntervalRef.current;
      const limit = BLOCK;
      
      // A série pode trocar (ativo/timeframe) enquanto o pedido está na rede: a resposta antiga é descartada,
      // senão velas do ativo anterior seriam coladas na série nova (cada série tem a sua SeriesWindow).
      const seriesAtStart = win, epoch = seriesEpochRef.current;
      const tNetStart = performance.now();
      const block = await candleSourceRef.current.fetchBlock({ symbol, interval, before: endTime, limit });
      const tNet = (performance.now() - tNetStart).toFixed(1);
      if (seriesRef.current !== seriesAtStart || seriesEpochRef.current !== epoch) {
        if (DBG_ENABLED) dbgEvent(`carga de histórico descartada: a série mudou (${symbol} ${interval}) durante o pedido`);
        return;
      }

      if (block.kind === 'erro') { failLoad(block.tipo, block.status, block.message, background && !manual, 'carga'); return; }
      clearLoadFailure();

      if (block.kind === 'ok') {
        // a fonte já entrega só as velas mais antigas que endTime (a sobreposta é removida, como antes)
        const filteredNew = block.candles as any[];
        {
          const tWinP1 = p1T(); // P1: etapa "janela de velas"
          win.prepend(filteredNew);
          // F3a: sem teto fixo; acima do limite da janela sai o lado do presente (nunca o bloco que acabou de chegar)
          trimWindow(win, 'oldest');
          p1End(P1S.WIN, tWinP1);
          // sem progresso (a vela mais antiga não ficou mais antiga): a fonte não tem nada mais antigo = fim
          if (!(win.oldestTime < endTime)) {
            if (DBG_ENABLED) dbgLoadDone({ ended: true, reason: 'sem progresso (vela mais antiga não mudou)' });
            knownStartRef.current = win.oldestTime;
            setHasReachedHistoryEnd(true);
            return;
          }
          const tWorkerStart = performance.now();
          const merged = win.toArray(), snapNewest = win.newestAbs;
          if (DBG_ENABLED) dbgMathCtx(true); // diagnóstico: o processMathData síncrono a seguir é de uma carga, não de um tick
          const processedPromise = processRawBinanceData(merged, { sma10Period, sma20Period, sma50Period });
          if (DBG_ENABLED) dbgMathCtx(false);
          const processed = await processedPromise;
          if (seriesRef.current !== win || seriesEpochRef.current !== epoch) return; // a série trocou durante o processamento
          const tWorker = (performance.now() - tWorkerStart).toFixed(1);
          const tTotal = (performance.now() - tStart).toFixed(1);
          
          console.info(`[HistoryLoad Telemetry] Fetched: ${filteredNew.length} candles | Network: ${tNet}ms | Worker(Hilbert/Math): ${tWorker}ms | Total: ${tTotal}ms | TotalHistory: ${win.length} candles`);
          if (DBG_ENABLED) dbgLoadDone({ ended: false, fetched: filteredNew.length, endTime: win.oldestTime, total: win.length, netMs: Number(tNet), procMs: Number(tWorker), totalMs: Number(tTotal) });
          navSource(SRC.CARGA_ANTIGA); commitProcessed(processed, snapNewest); navSource(0);
        }
      } else {
        // F3a: fim real só pela fonte (fimDosDados / vazio); o horário da primeira vela fica guardado
        if (DBG_ENABLED) dbgLoadDone({ ended: true, reason: block.kind === 'fimDosDados' ? 'nenhuma vela mais antiga que endTime' : 'resposta vazia' });
        knownStartRef.current = win.oldestTime;
        setHasReachedHistoryEnd(true);
      }
    } catch (e) {
      // erro no processamento (não na rede): também não é fim; a mesma regra de nova tentativa vale
      console.error("Failed to load more history:", e);
      failLoad('outro', null, String((e as any)?.message || e).slice(0, 60), background && !manual, 'carga');
    } finally {
      isLoadingHistoryRef.current = false;
      setIsLoadingHistory(false);
      if (DBG_ENABLED && background) dbgEvent(`pré-carga: fim após ${(performance.now() - tStart).toFixed(0)}ms (carregadas ${seriesRef.current?.length ?? 0} velas)`);
    }
  };

  /**
   * F3a C2: carrega o próximo bloco MAIS NOVO (a janela não chega ao presente: a vista voltou depois de um despejo do
   * lado do presente). A fonte só pede "antes de T": pede antes de (mais nova + 1.001 velas), que cobre o bloco
   * seguinte inteiro; velas repetidas são ignoradas pela janela. Bloco incompleto (ou pedido além do agora) = chegou ao
   * presente: a janela volta a receber as velas novas ao vivo.
   */
  const loadNewerHistory = async (background: boolean = false, manual: boolean = false) => {
    const win = seriesRef.current;
    if (!symbol || !win || win.length === 0 || windowAtPresentRef.current || isLoadingHistoryRef.current) return;
    if (switchRef.current) return;                           // F3b: troca pendente (veja loadMoreHistory)
    if (!manual && inRetryBackoff()) return;
    if (swProbe) swProbe.precarga(performance.now());
    isLoadingHistoryRef.current = true;
    loadBackgroundRef.current = background;
    setIsLoadingHistory(true);
    const tStart = performance.now();
    if (DBG_ENABLED) dbgLoadStart();
    try {
      const interval = activeIntervalRef.current;
      // C3: com cauda, o pedido não passa do começo dela; quando chega lá, a cauda inteira é costurada no fim
      const tail0 = tailRef.current.length ? tailRef.current[0][0] : null;
      const before = newerBlockBeforeWithTail(win.newestTime, win.intervalMs, tail0), epoch = seriesEpochRef.current;
      const block = await candleSourceRef.current.fetchBlock({ symbol, interval, before, limit: BLOCK });
      if (seriesRef.current !== win || seriesEpochRef.current !== epoch) return;
      if (block.kind === 'erro') { failLoad(block.tipo, block.status, block.message, background && !manual, 'carga (mais novas)'); return; }
      clearLoadFailure();
      const got = (block.kind === 'ok' ? block.candles : []) as any[][];
      const tWinP1 = p1T(); // P1: etapa "janela de velas" (junção/costura + despejo)
      const r = applyNewerBlock(win, tailRef.current, got, before, Date.now());
      const added = r.added, reachedPresent = r.reachedPresent;
      if (DBG_ENABLED) { dbgWindowNewerLoad(); if (r.stitched) dbgWindowStitch(r.tailLength); }
      if (DBG_ENABLED && r.stitched) dbgEvent(`costura: cauda ao vivo de ${r.tailLength} velas emendada → presente de volta na janela`);
      if (reachedPresent) setWindowAtPresent(true);
      const trimmed = trimWindow(win, 'newest');
      p1End(P1S.WIN, tWinP1);
      if (DBG_ENABLED) {
        dbgLoadDone({ ended: false, fetched: added, endTime: win.oldestTime, total: win.length, netMs: Number((performance.now() - tStart).toFixed(1)), procMs: 0, totalMs: 0 });
        dbgEvent(`carga (mais novas): +${added} velas${reachedPresent ? ', chegou ao presente' : ''} → ${win.length} velas`);
      }
      if (added > 0 || trimmed > 0) {
        const merged = win.toArray(), snapNewest = win.newestAbs;
        if (DBG_ENABLED) dbgMathCtx(true);
        const processed = await processRawBinanceData(merged, { sma10Period, sma20Period, sma50Period });
        if (DBG_ENABLED) dbgMathCtx(false);
        if (seriesRef.current !== win || seriesEpochRef.current !== epoch) return;
        navSource(r.stitched ? SRC.COSTURA : SRC.CARGA_NOVA); commitProcessed(processed, snapNewest); navSource(0);
      }
    } catch (e) {
      console.error("Failed to load newer history:", e);
      failLoad('outro', null, String((e as any)?.message || e).slice(0, 60), background && !manual, 'carga (mais novas)');
    } finally {
      isLoadingHistoryRef.current = false;
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (isLoadingHistory || isLoadingHistoryRef.current || !symbol || hasReachedHistoryEnd || inRetryBackoff()) return;
    if (preloadHoldRef.current) return;                     // F3c C4: troca em volta de T, até o 1º quadro da série nova
    
    const maxPossibleOffset = Math.max(0, (processedData?.length || 0) - 1000);
    // Auto-load more data when user scrolls into history and approaches the buffer boundary
    if (historyOffset > 0 && (maxPossibleOffset <= 500 || historyOffset >= maxPossibleOffset - 800)) {
      loadMoreHistory();
    }
  }, [historyOffset, realData?.length, isLoadingHistory, symbol, hasReachedHistoryEnd, retryTick, preloadGate]);

  const processedData = useRealData ? (realData || []) : generatedData;
  // F2: índice ancorado da vela mais nova desta série (NaN nos dados gerados): distâncias abaixo são relativas a ela
  const dataNewestAbs = newestAbsOf(processedData);

  // Handle data windowing for history navigation
  const intHistoryOffset = Math.floor(historyOffset);
  // Memoização rigorosa da fatia de dados, dependendo estritamente do offset inteiro
  const dataToRender = useMemo(() => {
    if (processedData.length === 0) return [];
    const windowSize = Math.min(processedData.length, 1000);
    const end = Math.max(windowSize, processedData.length - intHistoryOffset);
    const start = Math.max(0, end - windowSize);
    return processedData.slice(start, end);
  }, [processedData, intHistoryOffset]);

  // Limite estável da navegação: usa o menor dos dois tamanhos (caminho completo = velas brutas − aquecimento),
  // derivado da contagem de velas brutas, que não oscila. Assim o limite não pula 50 velas a cada tick ao vivo.
  const rawCandleCount = seriesRef.current?.length || 0;
  const navTotalPoints = useRealData && rawCandleCount > 0
    ? Math.min(processedData.length, rawCandleCount > MATH_WARMUP_DROP * 2 ? rawCandleCount - MATH_WARMUP_DROP : rawCandleCount)
    : processedData.length;
  const maxNavOffset = Math.max(0, navTotalPoints - 1000);
  // Harness de replay (só com ?dbg=1&replay=1): leitura do estado de navegação, em distância do fim, e gestos
  if (replay) {
    replay.nav = () => ({
      target: targetHistoryOffset.current, hist: historyOffsetRef.current, navTotal: navTotalPoints, maxNav: maxNavOffset,
      ended: hasReachedHistoryEnd, loading: isLoadingHistory, n: processedData.length, raw: seriesRef.current?.length || 0,
      ret: isReturningToLive, sym: symbol, viewFirstT: dataToRender[0]?.timestamp ?? null, viewLastT: dataToRender[dataToRender.length - 1]?.timestamp ?? null,
      price: currentPrice,
      // F3a: janela (presente dentro, cauda ao vivo), posição em índice ancorado (contínua) e rótulo do início
      atPresent: windowAtPresent, tail: tailRef.current.length, posIdx: navRef.current!.positionIndex, startHint: atSourceStart,
      // F3a.1: horário da vela mais nova da série (a parede no presente tem de chegar nela)
      seriesNewestT: seriesRef.current && seriesRef.current.length > 0 ? seriesRef.current.newestTime : null,
      // F3b: modo, roleta, série de origem dos dados na tela, horário da vela do centro, timeframe, troca em andamento
      mode: interactionModeRef.current, roulette: radialOpenRef.current, series: seriesOf(processedData),
      centerT: momentoDoCentro(processedData as any, positionFor(historyOffsetRef, dataNewestAbs)), tf: timeframe, switching: histRuntime.switching,
    });
    // F3b: armar pelo replay passa pelo mesmo árbitro do dedo (não arma com a roleta aberta nem com troca pendente)
    replay.setSymbol = setSymbol; replay.setTimeframe = setTimeframe;
    replay.setMode = (m: any) => { if (m === 'time') histEvent('armar'); else setInteractionMode(m); };
    // F3b: caminhos do usuário (roleta, X, volta instantânea) e leituras do momento do centro e do modo
    replay.openRoulette = () => openRadialMenu({ x: 100, y: 100 });
    replay.chooseTimeframe = (tf: string) => { handleUserSetTimeframe(tf); setRadialMenuPos(null); setActiveGesture(null); };
    replay.closeRoulette = () => { setRadialMenuPos(null); setActiveGesture(null); };
    replay.exitX = () => histEvent('x');
    // arrasto pelo caminho do dedo: só no modo histórico, sem autoplay e sem a roleta (o gesto marca 'time_drag')
    replay.dragGesture = (v: number) => {
      if (interactionModeRef.current !== 'time' || isReturningToLiveRef.current || radialOpenRef.current) return;
      if (useFlowaesStore.getState().activeGesture !== 'time_drag') setActiveGesture('time_drag');
      replay.drag?.(v);
    };
    replay.irParaTempo = irParaTempo;
    replay.seriesCheck = () => { const w = seriesRef.current; return w ? { ...checkSeries(w.toArray(), w.intervalMs), limit: windowLimitRef.current } : null; };
  }
  // limite da navegação guardado como índice ancorado (a fronteira continua lendo em distância do fim)
  useEffect(() => { navRef.current!.setLimitFor(processedData.length > 0 ? maxNavOffset : null, dataNewestAbs); }, [maxNavOffset, processedData.length > 0, dataNewestAbs]);

  // Correção A (re-âncora): com o fim do histórico definido, alvo e posição não podem ficar além
  // das velas carregadas. Sem isso, o que foi arrastado "a mais" vira uma dívida que trava a volta
  // (manual e autoplay). Só encosta no limite estável; não altera sensibilidade nem velocidades.
  useEffect(() => {
    if (!hasReachedHistoryEnd) return;
    const max = maxNavOffset; // mesmo limite que SceneContent recebe (navTotalPoints − 1000)
    // alvo e posição relativos à mesma série do limite (F2)
    const before = targetFor(targetHistoryOffset, dataNewestAbs);
    navSource(SRC.TRAVA);
    if (before > max) setTargetFor(targetHistoryOffset, max, dataNewestAbs);
    if (positionFor(historyOffsetRef, dataNewestAbs) > max) setPositionFor(historyOffsetRef, max, dataNewestAbs);
    navSource(0);
    if (DBG_ENABLED && before > max) dbgEvent(`re-âncora: alvo ${before.toFixed(0)} → ${max} (maxPossibleOffset)`);
  }, [hasReachedHistoryEnd, maxNavOffset]);

  // F0: o gesto encostou na borda carregada e o próximo bloco está na rede (a tela espera, sem salto)
  const waitingAtEdge = interactionMode === 'time' && !isReturningToLive && isLoadingHistory &&
    ((!hasReachedHistoryEnd && isAtLoadedEdge(historyOffset, navTotalPoints)) || (!windowAtPresent && historyOffset <= 1));
  // F3a: rótulo "início dos dados" quando a vela mais antiga da fonte está na tela. F3a.1 C6: em qualquer modo (o modo
  // tempo volta sozinho para órbita depois de 10 s parado e a vista continua no início)
  const atSourceStart = useRealData && hasReachedHistoryEnd && historyOffset >= maxNavOffset - 1;
  // F3e C4: "início dos dados" é um pulso de ~3 s: ao encostar na borda mais antiga com o fim real confirmado, ou ao
  // armar a navegação a <= START_NEAR velas do início. Não aparece na abertura só porque a série inteira cabe na tela.
  const [inicioAte, setInicioAte] = useState(0);
  const inicioTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pulseInicio = () => {
    setInicioAte(performance.now() + START_PULSE_MS);
    if (inicioTimerRef.current) clearTimeout(inicioTimerRef.current);
    inicioTimerRef.current = setTimeout(() => { inicioTimerRef.current = null; setInicioAte(0); }, START_PULSE_MS);
  };
  const onOldestEdge = () => { if (hasReachedHistoryEnd) pulseInicio(); };
  const prevModeForStartRef = useRef(interactionMode);
  useEffect(() => {
    const was = prevModeForStartRef.current; prevModeForStartRef.current = interactionMode;
    if (was !== 'time' && interactionMode === 'time' && useRealData && hasReachedHistoryEnd && maxNavOffset - historyOffset <= START_NEAR) pulseInicio();
  }, [interactionMode]);
  // encostou antes de a fonte confirmar o fim (a carga daquela borda ainda estava na rede): o pulso vem na confirmação
  useEffect(() => {
    if (hasReachedHistoryEnd && useRealData && interactionMode === 'time' && historyOffset >= maxNavOffset - 1) pulseInicio();
  }, [hasReachedHistoryEnd]);
  // F3e C4: a mensagem de histórico (uma por vez)
  const histMsg = mensagemHistorico({
    falha: useRealData && loadFailure && !(hasReachedHistoryEnd && loadFailure.lado === 'older') ? loadFailure : null,
    distBorda: !loadFailure ? Infinity : loadFailure.lado === 'older' ? maxNavOffset - historyOffset : loadFailure.lado === 'newer' ? (windowAtPresent ? Infinity : historyOffset) : 0,
    trocando: switching ? `${symbol} ${timeframe}` : null, progresso: switchProgress,
    esperandoBorda: waitingAtEdge, inicioAte, agora: performance.now(), fonte: candleSourceRef.current.name,
  });
  useEffect(() => { if (msgProbe) msgProbe.mudou(performance.now(), histMsg, histMsg?.tipo === 'erro' ? loadFailRef.current?.status ?? null : null); }, [histMsg?.tipo, histMsg?.texto]);
  const timeBannerOn = interactionMode === 'time' && !isReturningToLive;
  /** "tentar de novo": a troca pendente, ou a carga que falhou (do lado que falhou) */
  const retryHistory = () => {
    if (retrySwitchRef.current) { retrySwitchRef.current(); return; }
    const lado = loadFailRef.current?.lado;
    clearLoadFailure();
    if (lado === 'newer') loadNewerHistory(false, true); else loadMoreHistory(false, true);
  };

  // Diagnóstico (?dbg=1): quanto tempo a tela ficou parada esperando bloco na borda carregada
  const edgeWaitStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (!DBG_ENABLED) return;
    if (waitingAtEdge) { if (edgeWaitStartRef.current === null) edgeWaitStartRef.current = performance.now(); }
    else if (edgeWaitStartRef.current !== null) { const ms = performance.now() - edgeWaitStartRef.current; dbgEvent(`tela parada por espera de carga: ${ms.toFixed(0)} ms`); dbgWindowWait(ms); edgeWaitStartRef.current = null; }
  }, [waitingAtEdge]);

  // ---- F0: pré-carga do histórico ----
  // Liga ao armar a navegação (MÁQUINA DO TEMPO) e PRELOAD_IDLE_MS depois da carga inicial, se não houver gesto.
  // Desliga ao trocar de ativo/timeframe (a pré-carga recomeça na série nova; respostas da série antiga já são
  // descartadas pela guarda de série em loadMoreHistory).
  const [preloadOn, setPreloadOn] = useState(false);
  useEffect(() => { setPreloadOn(false); }, [symbol, timeframe, useRealData]);
  useEffect(() => { if (interactionMode === 'time') setPreloadOn(true); }, [interactionMode, symbol, timeframe]);
  useEffect(() => {
    if (!useRealData || isLoadingData || preloadOn) return;
    let id: ReturnType<typeof setTimeout>;
    const arm = () => {
      id = setTimeout(() => {
        if (useFlowaesStore.getState().activeGesture === 'time_drag') arm(); else setPreloadOn(true);
      }, PRELOAD_IDLE_MS);
    };
    arm();
    return () => clearTimeout(id);
  }, [useRealData, isLoadingData, preloadOn, symbol, timeframe]);
  // F3a C2: pré-carga nas duas direções (windowPolicy.preloadNeed): à frente do movimento ao menos 3 blocos (5 parado),
  // do outro lado ao menos KEEP. Um pedido de cada vez (guarda de carga atual). O lado do passado para no início real
  // dos dados; o do presente só existe enquanto a janela não chega ao presente.
  const lastMotionRef = useRef(0);
  useEffect(() => {
    const mv = motionOf(historyOffsetRef), d = mv - lastMotionRef.current;
    lastMotionRef.current = mv;
    if (d > 0.5) moveDirRef.current = -1; else if (d < -0.5) moveDirRef.current = 1;
  }, [historyOffset]);
  useEffect(() => {
    if (!preloadOn || !useRealData || isLoadingData || !symbol || isLoadingHistory || isLoadingHistoryRef.current) return;
    if (preloadHoldRef.current) return;                     // F3c C4: até o primeiro quadro da série nova
    if (!seriesRef.current || seriesRef.current.length === 0) return;
    if (inRetryBackoff()) return;
    const pos = positionFor(historyOffsetRef, dataNewestAbs);
    const idle = !isReturningToLive && useFlowaesStore.getState().activeGesture !== 'time_drag';
    const need = preloadNeed(maxNavOffset - pos, windowAtPresent ? Infinity : pos, moveDirRef.current, idle, hasReachedHistoryEnd);
    if (DBG_ENABLED) dbgWindowAhead(maxNavOffset - pos, windowAtPresent ? Infinity : pos);
    if (need.first === 'older') loadMoreHistory(true);
    else if (need.first === 'newer') loadNewerHistory(true);
  }, [preloadOn, historyOffset, maxNavOffset, isLoadingHistory, hasReachedHistoryEnd, isLoadingData, symbol, useRealData, retryTick, windowAtPresent, isReturningToLive, preloadGate]);

  // Diagnóstico (?dbg=1): registra mudanças do limite de navegação.
  const lastMaxNavOffsetRef = useRef<number | null>(null);
  useEffect(() => {
    if (!DBG_ENABLED) return;
    const prev = lastMaxNavOffsetRef.current;
    lastMaxNavOffsetRef.current = maxNavOffset;
    if (prev !== null && prev !== maxNavOffset) dbgEvent(`limite: maxPossibleOffset ${prev} → ${maxNavOffset} (total ${processedData.length})`);
  }, [maxNavOffset]);

  // Extração rápida de primitivos do último candle para o HUD (evita passar array de 4000 itens)
  const lastProcessedCandle = processedData.length > 0 ? processedData[processedData.length - 1] : null;
  const currentMomentum = lastProcessedCandle?.momentum ?? null;
  const candleCloseTime = lastProcessedCandle?.closeTime;

  useEffect(() => {
    // Don't clear data immediately when switching symbol/timeframe/mode,
    // let it retain old data visible while it loads the new one to avoid blinking
    pointsNeedUpdateRef.current = false;
    setDataError(null);

    if (useRealData) {
       // F3b C2: o momento do CENTRO da vista na série que ainda está na tela (antes de re-ancorar); no presente, null
       let switchT: number | null = null;
       if (realData && realData.length > 0) {
         // presente = a janela contém o presente e a vista está a menos de 1 vela do fim; no fim de uma janela no
         // passado o momento do centro vale (senão a troca abria no presente)
         const pos = positionFor(historyOffsetRef, newestAbsOf(realData));
         if (pos >= 1 || !windowAtPresentRef.current) switchT = momentoDoCentro(realData as any, pos);
       }
       if (forcePresentRef.current) { forcePresentRef.current = false; switchT = null; }   // C5: irParaTempo('agora')
       // Decisão do Mateus (01/11): trocar de ATIVO abre sempre no presente (sem T), pelo caminho da troca no presente;
       // a troca de TIMEFRAME continua no mesmo momento, centrada
       if (seriesSymbolRef.current !== null && seriesSymbolRef.current !== symbol) switchT = null;
       seriesSymbolRef.current = symbol;
       // F3b C3: a troca fecha a navegação histórica (e o autoplay) antes; o momento do centro vai para a série nova
       histEvent('trocaInicio');
       // série nova: a vela mais nova volta a ser o índice ancorado 0 (a distância do fim exibida não muda)
       navRef.current!.reanchor();
       // F3d C6 (?dbg=1): a re-âncora baixa o índice do alvo sem mover a vista; o detector de RECUO compara índices
       // com o quadro anterior: a queda vai para "RECUOs por troca de série (excluídos)" e a base do detector é acertada
       if (DBG_ENABLED) {
         const h = historyOffsetRef as any, i = navRef.current!.targetIndex;
         if (h.lastTargetIdx !== undefined && i < h.lastTargetIdx - 0.5) dbgWindowRecuoTroca(h.lastTargetIdx - i);
         h.lastTargetIdx = i;
       }
       seriesEpochRef.current++;
       pendingLiveRef.current = 0;
       knownStartRef.current = null;
       tailRef.current = [];
       const dirBefore = moveDirRef.current;                  // F3c C4: direção da vista antes da troca
       moveDirRef.current = 0;
       preloadHoldRef.current = switchT !== null;
       setWindowAtPresent(true);
       let isSubscribed = true;
       let activeWs: WebSocket | null = null;
       let activeAnimationFrame: number | null = null;
       let switchRetryTimer: ReturnType<typeof setTimeout> | null = null;
       const hadData = !!(realData && realData.length > 0);
       switchRef.current = { T: switchT, epoch: seriesEpochRef.current, dir: dirBefore };
       p1SeriesSwitch(true);
       setSwitching(hadData);
       histRuntime.switching = hadData;
       if (DBG_ENABLED && hadData) dbgEvent(`troca de série: ${switchT === null ? 'no presente' : 'centro ' + new Date(switchT).toISOString().slice(0, 16)}`);
       const swReg = swProbe && hadData ? swProbe.start(performance.now(), seriesLabelRef.current, `${symbol} ${timeframe}`, switchT) : null; // F3c C1 (?dbg=1)
       seriesLabelRef.current = `${symbol} ${timeframe}`;

       setBackwallStyle('candles');
       isLoadingDataRef.current = true;
       setIsLoadingData(true);
       setHasReachedHistoryEnd(false);
       clearLoadFailure();
       
       let interval = '1d';
       if (timeframe === '1m') interval = '1m';
       else if (timeframe === '3m') interval = '3m';
       else if (timeframe === '5m') interval = '5m';
       else if (timeframe === '15m') interval = '15m';
       else if (timeframe === '20m') interval = '15m'; // Binance doesn't have 20m, use 15m
       else if (timeframe === '30m') interval = '30m';
       else if (timeframe === '1h') interval = '1h';
       else if (timeframe === '2h') interval = '2h';
       else if (timeframe === '4h') interval = '4h';
       else if (timeframe === '6h') interval = '6h';
       else if (timeframe === '8h') interval = '8h';
       else if (timeframe === '12h') interval = '12h';
       else if (timeframe === '1d') interval = '1d';
       else if (timeframe === '3d') interval = '3d';
       else if (timeframe === '1w') interval = '1w';
       else if (timeframe === '1y' || timeframe === '1M') interval = '1M';
       
       if (!symbol || symbol.length < 3) return;

       activeIntervalRef.current = interval;
       
              let isProcessing = false;
       
       const processSnapshot = async (snapshot: any[], isInit: boolean = false, snapNewestAbs?: number) => {
           if (!isSubscribed) return;
           try {
               isProcessing = true;
               const tCalc = isInit && swProbe ? performance.now() : 0;
               const processed = await processRawBinanceData(snapshot, { sma10Period, sma20Period, sma50Period });
               if (isInit && swProbe) swProbe.calc(performance.now() - tCalc, snapshot.length); // F3d C5 (?dbg=1)
               if (!isSubscribed) return;
               
               if (processed && processed.length > 0) {
                   if (isInit) placeAtSwitch(processed, intervalToMs(interval));
                   navSource(isInit ? SRC.TROCA : SRC.VIVO); commitProcessed(processed, snapNewestAbs); navSource(0);
               } else {
                   setRealData(null);
                   if (!seriesRef.current) {
                       setDataError("Dados de mercado indisponíveis.");
                   }
               }
           } catch (err: any) {
               if (!isSubscribed) return;
               setDataError(err.message);
           } finally {
               if (isSubscribed) {
                   isProcessing = false;
                   if (isInit) {
                       isLoadingDataRef.current = false;
                       setIsLoadingData(false);
                   }
               }
           }
       };

       // F3b C2: carga inicial pela CandleSource EM VOLTA do momento da troca (sem momento: as INITIAL_CANDLES mais
       // novas, o pedido de antes). Falha com a série anterior na tela: aviso com "tentar de novo" e nova tentativa
       // com recuo (falha ≠ fim); sem série anterior, o erro de antes.
       const loadLimit = switchT === null ? INITIAL_CANDLES : AROUND_CANDLES;
       // carregador do F3b (presente, e a reserva da grade: busca do início do ativo com confirmação)
       const loadAround = (): Promise<SerieEmVolta> => {
         if (swProbe) swProbe.sent(swReg, performance.now(), switchT === null ? 'presente' : 'em volta', 0, 0); // F3c C1 (?dbg=1)
         return carregarEmVolta(candleSourceRef.current, { symbol, interval, intervalMs: intervalToMs(interval), T: switchT, now: Date.now(), limit: loadLimit }).then(block => {
           if (swProbe) { swProbe.sent(swReg, performance.now(), switchT === null ? 'presente' : 'em volta', block.requests, block.requests * pesoDoPedido(loadLimit)); swProbe.resposta(swReg, performance.now(), block.kind !== 'erro'); }
           return block;
         });
       };
       // F3d C2/C3: troca (com a série anterior na tela, no passado ou no presente): a JANELA FINAL (~10.000 velas em
       // volta de T, ou terminando no presente) pelos blocos da grade, em paralelo (até 6 em voo); a série nova só
       // aparece com tudo carregado e a força calculada uma vez sobre ela. Bloco curto = começo do ativo: o carregador
       // do F3b quando o que chegou não basta. A carga inicial do app (sem série anterior) continua a de antes.
       // F3d C4: UMA carga por troca; ela mesma repete só o pedido que falhou (2 s, 4 s) e desiste com 3 falhas ou 12 s;
       // aí o gráfico anterior fica com "tentar de novo", que pede só o que falta. Outra troca cancela esta.
       let carga: CargaGrade | null = null;
       const loadGrid = (): Promise<SerieEmVolta> => {
         if (!carga) {
           carga = new CargaGrade(candleSourceRef.current, { symbol, interval, intervalMs: intervalToMs(interval), T: switchT, now: Date.now(), window: windowLimitRef.current, dir: dirBefore });
           if (swProbe) carga.onResposta = (_q, ok) => swProbe!.resposta(swReg, performance.now(), ok);
           carga.onProgresso = (n, m) => { if (isSubscribed) setSwitchProgress(`${n}/${m}`); };
         }
         setSwitchProgress(`${carga.feitos()}/${carga.plano.length}`);
         if (swProbe) { const pend = carga.pendentes(); swProbe.sent(swReg, performance.now(), 'janela', pend.length, pend.reduce((w, q) => w + pesoDoPedido(q.limit), 0)); }
         return carga.run().then(r => (r === 'reserva' ? loadAround() : r));
       };
       const attemptLoad = () => {
         return (hadData ? loadGrid() : loadAround()).then(block => {
         if (!isSubscribed) return;
         if (block.kind === 'erro') {
           if (hadData) {
             failLoad(block.tipo, block.status, block.message, false, 'troca de série');
             if (carga) return;                               // F3d C4: as repetições já foram feitas; agora é o "tentar de novo"
             const delay = Math.max(0, (loadFailRef.current?.retryAt ?? performance.now()) - performance.now());
             if (switchRetryTimer) clearTimeout(switchRetryTimer);
             switchRetryTimer = setTimeout(() => { switchRetryTimer = null; attemptLoad(); }, delay);
             return;
           }
           setDataError(block.message || "Erro ao conectar com o mercado.");
           setIsLoadingData(false);
           return;
         }
         retrySwitchRef.current = null;
         clearLoadFailure();
         if (DBG_ENABLED && block.requests > 1) dbgEvent(`troca de série: ${block.requests} pedidos${block.startKnown !== null ? ' (início do ativo encontrado)' : ''}`);
         const res = block.candles;
         if (res && res.length > 0) {
            const win = new SeriesWindow(symbol, interval, intervalToMs(interval));
            win.open(res);
            seriesRef.current = win;
            // F3b: janela em volta de T pode não conter o presente (as velas ao vivo vão para a cauda); início da fonte
            // já confirmado na busca (T antes do início do ativo)
            setWindowAtPresent(block.atPresent);
            if (block.startKnown !== null) { knownStartRef.current = block.startKnown; setHasReachedHistoryEnd(true); }
            if (switchT !== null) setPreloadOn(true);
            processSnapshot(win.toArray(), true, win.newestAbs);
            
            // Set up WebSocket for real-time updates
            const traditionalMarkets = ['US100', 'US500', 'AAPL', 'TSLA', 'NVDA', 'EURUSD'];
            if (!traditionalMarkets.includes(symbol.toUpperCase())) {
                let wsSymbol = symbol.toLowerCase();
                if (wsSymbol === 'xauusdt' || wsSymbol === 'xauusd') wsSymbol = 'paxgusdt';
                
                const wsUrl = `wss://data-stream.binance.vision/stream?streams=${wsSymbol}@kline_${interval}/${wsSymbol}@aggTrade`;
                
                const connectWs = () => {
                   if (!isSubscribed) return;
                   activeWs = new WebSocket(wsUrl);
                   activeWs.onmessage = (event) => {
                      if (!isSubscribed) return;
                      const msg = JSON.parse(event.data);
                      
                      const win = seriesRef.current;
                      // F3a C3: com o presente fora da janela, as velas ao vivo vão para a cauda separada (a janela não
                      // cresce nem desloca a vista); a costura acontece quando a vista volta (loadNewerHistory)
                      if (msg.stream && win && win.length > 0 && !windowAtPresentRef.current) {
                          if (msg.stream.endsWith(`@kline_${interval}`)) {
                              const k = msg.data.k;
                              tailPush(tailRef.current, [k.t, k.o, k.h, k.l, k.c, k.v, k.T], win.intervalMs);
                          } else if (msg.stream.endsWith('@aggTrade')) {
                              const p = msg.data.p, tradeTime = msg.data.E || msg.data.T || Date.now();
                              setLatencyMs(Math.max(0, Date.now() - tradeTime));
                              const last = tailRef.current[tailRef.current.length - 1];
                              if (last) {
                                  last[4] = p;
                                  if (parseFloat(p) > parseFloat(last[2])) last[2] = p;
                                  if (parseFloat(p) < parseFloat(last[3])) last[3] = p;
                              }
                          }
                      } else if (msg.stream && win && win.length > 0 && windowAtPresentRef.current) {
                          const streamName = msg.stream;
                          const lastCandle = win.at(win.newestAbs) as any[];
                          
                          if (streamName.endsWith(`@kline_${interval}`)) {
                              const k = msg.data.k;
                              const newCandle = [k.t, k.o, k.h, k.l, k.c, k.v, k.T];
                              
                              if (k.t === lastCandle[0]) {
                                 win.append([newCandle]);          // mesma vela em formação: substitui
                              } else if (k.t > lastCandle[0]) {
                                 win.append([newCandle]);
                                 // F3a: sem o shift de antes; acima do limite sai um bloco do lado mais distante da vista
                                 trimWindow(win);
                                 // vela nova ao vivo: entra no índice ancorado quando a série processada com ela for entregue
                                 pendingLiveRef.current += 1;
                              }
                              pointsNeedUpdateRef.current = true;
                          } else if (streamName.endsWith('@aggTrade')) {
                              const p = msg.data.p;
                              const tradeTime = msg.data.E || msg.data.T || Date.now();
                              const latency = Date.now() - tradeTime;
                              setLatencyMs(Math.max(0, latency));
                              // Update the last candle's close price with the exact real-time trade price
                              if (lastCandle) {
                                  lastCandle[4] = p; // update close
                                  // Update high/low if current price exceeds them
                                  if (parseFloat(p) > parseFloat(lastCandle[2])) lastCandle[2] = p;
                                  if (parseFloat(p) < parseFloat(lastCandle[3])) lastCandle[3] = p;
                              }
                              pointsNeedUpdateRef.current = true;
                          }
                      }
                   };
                   activeWs.onclose = () => {
                      if (isSubscribed) {
                         console.log("Binance WS closed, reconnecting silently...");
                         setTimeout(connectWs, 3000);
                      }
                   };
                   activeWs.onerror = () => {
                      if (isSubscribed && activeWs) {
                         activeWs.close();
                      }
                   };
                };
                connectWs();
            }
            
            let lastUpdateTime = 0;
            const updateLoop = (timestamp: number) => {
                if (!isSubscribed) return;
                
                // Throttle updates to ~2 FPS (500ms) to maintain smooth UI performance.
                if (timestamp - lastUpdateTime > 500) {
                    const win = seriesRef.current;
                    if (pointsNeedUpdateRef.current && win && win.length > 0 && !isProcessing) {
                       const snapshot = win.toArray();
                       pointsNeedUpdateRef.current = false;
                       lastUpdateTime = timestamp;
                       processSnapshot(snapshot, false, win.newestAbs);
                    }
                }
                activeAnimationFrame = requestAnimationFrame(updateLoop);
            };
            activeAnimationFrame = requestAnimationFrame(updateLoop);

         } else {
            setDataError("Não foi possível carregar os dados reais.");
            setIsLoadingData(false);
         }
       })
       .catch((err: any) => {
         if (isSubscribed) {
           setDataError(err instanceof Error ? err.message : "Erro ao conectar com o mercado.");
           setIsLoadingData(false);
         }
       });
       };
       retrySwitchRef.current = () => { if (switchRetryTimer) { clearTimeout(switchRetryTimer); switchRetryTimer = null; } clearLoadFailure(); attemptLoad(); };
       attemptLoad();
       
       return () => {
          isSubscribed = false;
          if (switchRetryTimer) clearTimeout(switchRetryTimer);
          if (carga) carga.cancel();                         // F3d C4: outra troca: nada mais desta é pedido nem repetido
          
          if (activeWs) {
             activeWs.onmessage = null;
             activeWs.close();
          }
          if (activeAnimationFrame) cancelAnimationFrame(activeAnimationFrame);
       };
    }
  }, [useRealData, timeframe, symbol, presentNonce]);


  const currentPrice = dataToRender.length > 0 ? (dataToRender[dataToRender.length - 1].close ?? dataToRender[dataToRender.length - 1].price) : 100;
  const lastPrice = currentPrice;
  const currentResonance = dataToRender.length > 0 ? (dataToRender[dataToRender.length - 1].resonance ?? 0) : 0;
  const priceChange = dataToRender.length > 0 ? ((currentPrice - (dataToRender[0].open ?? dataToRender[0].price)) / (dataToRender[0].open ?? dataToRender[0].price)) * 100 : 0;
  
  const periodHigh = Math.max(...dataToRender.map(d => d.high ?? d.price));
  const periodLow = Math.min(...dataToRender.map(d => d.low ?? d.price));
  const periodChange = priceChange;

  const timeframeNames: Record<string, string> = {
    '1m': '1 Minuto / Ultra Detail',
    '3m': '3 Minutos / High Detail',
    '5m': '5 Minutos / High Detail',
    '15m': '15 Min / Intraday',
    '20m': '20 Min / Intraday',
    '30m': '30 Min / Intraday',
    '1h': '1 Hora / Mid Detail',
    '2h': '2 Horas / Mid Detail',
    '4h': '4 Horas / Macro',
    '6h': '6 Horas / Macro',
    '8h': '8 Horas / Macro',
    '12h': '12 Horas / Macro',
    '1d': '1 Dia / Low Detail',
    '3d': '3 Dias / Macro Wave',
    '1w': '1 Semana / Macro Wave',
    '1M': '1 Mês / Global Trend',
    '1y': '1 Ano / Trend Line',
  };

  

  const isTutorialMode = isInitialGlideComplete && !hasInteracted;
  const showUiControls = hasInteracted && !isCurrentlyIdle && uiHiddenLevel === 0 && !isEditingSymbol;

  const isDevAdminRoute = window.location.pathname === '/dev-admin';

  const authOverlayElement = (
    <AuthOverlay
      isDevAdminRoute={isDevAdminRoute}
      setShowGodPanel={setShowGodPanel}
      isAuthLoading={isAuthLoading}
      user={user}
      isSubscriptionActive={isSubscriptionActive}
      setIsSubscriptionActive={setIsSubscriptionActive}
      authStatus={authStatus}
      authToken={authToken}
      setAuthToken={setAuthToken}
      authError={authError}
      setAuthError={setAuthError}
      setAuthStatus={setAuthStatus}
      handleLogin={handleLogin}
      showLoginLogoutConfirm={showLoginLogoutConfirm}
      setShowLoginLogoutConfirm={setShowLoginLogoutConfirm}
      handleLogout={handleLogout}
    />
  );

  // Deterministic user-interaction handlers for auto-saving key settings
  const handleUserSetSymbol = useCallback((newSymbol: string) => {
    setSymbol(newSymbol); // Note: symbol is intentionally excluded from cloud sync to allow independent local sessions per device.
  }, [setSymbol]);

  const handleUserSetTimeframe = useCallback((newTimeframe: string) => {
    setTimeframe(newTimeframe); // Note: timeframe is intentionally excluded from cloud sync to allow independent local sessions per device.
  }, [setTimeframe]);

  const handleUserSetVisibleTimeframes = useCallback((newVisible: string[]) => {
    setVisibleTimeframes(newVisible);
    // Note: 'visibleTimeframes' is intentionally synced to the cloud (unlike symbol/timeframe).
    // This ensures the user's customized radial menu options are persisted across all their devices.
    if (user && authStatus === "authenticated") {
      const docRef = doc(db, 'users', user.uid);
      setDoc(docRef, { visibleTimeframes: newVisible }, { merge: true }).catch(console.error);
    }
  }, [user, authStatus, setVisibleTimeframes, db]);

  if (isDevAdminRoute && !isAdmin) return authOverlayElement;
  if (isAuthLoading && !isAdmin) return authOverlayElement;
  if (!isAdmin && (!user || !isSubscriptionActive)) return authOverlayElement;
  if (authStatus !== "authenticated" && authStatus !== "reconnecting" && !isAdmin) return authOverlayElement;

  return (
    <>
            <div 
        className={cn("w-screen h-[100dvh] bg-[#02040a] relative font-mono text-[#e0e0e0] overflow-hidden select-none touch-none")}
        onPointerDown={(e) => {
          isPointerDownRef.current = true;
          isPointerDownRef.current = true;
          if (e.button === 1) { // Middle click
            e.preventDefault();
          openRadialMenu({ x: e.clientX, y: e.clientY });
          return;
        }

        if (e.button !== 0) return;

        const isInteractive = !!(
            (e.target as HTMLElement).closest?.('button') || 
            (e.target as HTMLElement).closest?.('input') || 
            (e.target as HTMLElement).closest?.('select') || 
            (e.target as HTMLElement).closest?.('a') || 
            (e.target as HTMLElement).closest?.('[role="button"]') ||
            (e.target as HTMLElement).closest?.('.cursor-pointer') ||
            (e.target as HTMLElement).closest?.('.pointer-events-auto')
        );
        if (isInteractive) return;

        const now = Date.now();
        const lastDownTime = lastTapRef.current || 0;
        const tapDelay = 350;
        
        const lastDownPos = lastTapPosRef.current;
        let tapDist = 0;
        if (lastDownPos) {
          tapDist = Math.hypot(e.clientX - lastDownPos.x, e.clientY - lastDownPos.y);
        }

                if (now - lastDownTime < tapDelay && (!lastDownPos || tapDist < 30)) {
           // Second tap down
           const cx = e.clientX;
           const cy = e.clientY;
           setActiveGesture?.('radial_menu');
           incrementGestureVersion?.();
           if (radialMenuTimerRef.current) clearTimeout(radialMenuTimerRef.current);
           radialMenuTimerRef.current = setTimeout(() => {
             if (isPointerDownRef.current) {
               openRadialMenu({ x: cx, y: cy });
               setActiveGesture?.('radial_menu');
               incrementGestureVersion?.();
               document.dispatchEvent(new PointerEvent("pointerup", { bubbles: true }));
               document.dispatchEvent(new PointerEvent("pointercancel", { bubbles: true }));
             }
           }, 250); // wait 250ms to see if they hold
           radialMenuStartPosRef.current = { x: cx, y: cy };
           lastTapRef.current = 0;
           lastTapPosRef.current = null;
        } else {
           lastTapRef.current = now;
           lastTapPosRef.current = { x: e.clientX, y: e.clientY };
        }
      }}
      onPointerMove={(e) => {
        if (radialMenuTimerRef.current && radialMenuStartPosRef.current) {
          const dist = Math.hypot(e.clientX - radialMenuStartPosRef.current.x, e.clientY - radialMenuStartPosRef.current.y);
          if (dist > 15) {
            clearTimeout(radialMenuTimerRef.current);
            radialMenuTimerRef.current = null;
            setActiveGesture?.(null);
          }
        }
      }}
      onPointerUp={() => { 
        isPointerDownRef.current = false; 
        setActiveGesture?.(null);
        if (radialMenuTimerRef.current) {
          clearTimeout(radialMenuTimerRef.current);
          radialMenuTimerRef.current = null;
        }
      }}
      onPointerCancel={() => { 
        isPointerDownRef.current = false;
        setActiveGesture?.(null);
        if (radialMenuTimerRef.current) {
          clearTimeout(radialMenuTimerRef.current);
          radialMenuTimerRef.current = null;
        }
      }}
      onTouchStart={(e) => {
        if (e.touches.length === 1) {
          const touch = e.touches[0];
          const startTarget = touch.target as HTMLElement;
          const isInteractive = !!(
            startTarget.closest('button') || 
            startTarget.closest('input') || 
            startTarget.closest('select') || 
            startTarget.closest('a') || 
            startTarget.closest('[role="button"]') ||
            startTarget.closest('.cursor-pointer') ||
            startTarget.closest('.pointer-events-auto')
          );
          if (isInteractive) {
            touchStartPosRef.current = null;
            touchStartTimeRef.current = 0;
          } else {
            touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
            touchStartTimeRef.current = Date.now();
          }
        } else {
          touchStartPosRef.current = null;
          touchStartTimeRef.current = 0;
          lastPinchTimeRef.current = Date.now();
        }
      }}
      onTouchEnd={(e) => {
        if (!touchStartPosRef.current || touchStartTimeRef.current === 0) {
          return;
        }
        if (Date.now() - lastPinchTimeRef.current < 500) {
          touchStartPosRef.current = null;
          touchStartTimeRef.current = 0;
          return;
        }
        if (e.changedTouches.length === 1) {
          
          
          

          // Removed double tap logic here since it's now handled globally in onPointerDown
        }
        touchStartPosRef.current = null;
        touchStartTimeRef.current = 0;
      }}
    >
      <BackWall2DChart
        isOpen={isBackWallChartOpen}
        onClose={() => {
          setIsBackWallChartOpen(false);
          setActiveGesture(null);
        }}
        data={isBackWallChartOpen ? processedData : []}
        symbol={symbol}
        timeframe={timeframe}
        isEditingSymbol={isEditingSymbol}
        setIsEditingSymbol={setIsEditingSymbol}
        setSymbol={handleUserSetSymbol}
        lastPrice={lastPrice}
        priceChange={priceChange}
        latencyMs={latencyMs}
      />
      <Floor2DChart
        isOpen={isFloorChartOpen}
        onClose={() => {
          setIsFloorChartOpen(false);
          setActiveGesture(null);
        }}
        data={isFloorChartOpen ? processedData : []}
        symbol={symbol}
        timeframe={timeframe}
        isEditingSymbol={isEditingSymbol}
        setIsEditingSymbol={setIsEditingSymbol}
        setSymbol={handleUserSetSymbol}
        lastPrice={lastPrice}
        priceChange={priceChange}
        latencyMs={latencyMs}
      />
      <SideWall2DChart
        isOpen={isSideChartOpen}
        onClose={() => {
          setIsSideChartOpen(false);
          setActiveGesture(null);
        }}
        data={isSideChartOpen ? processedData : []}
        symbol={symbol}
        timeframe={timeframe}
        isEditingSymbol={isEditingSymbol}
        setIsEditingSymbol={setIsEditingSymbol}
        setSymbol={handleUserSetSymbol}
        lastPrice={lastPrice}
        priceChange={priceChange}
        latencyMs={latencyMs}
      />

      {authStatus === "reconnecting" && (
         <div className="absolute top-32 md:top-36 left-1/2 transform -translate-x-1/2 z-[2000] bg-[#00ffa3]/10 border border-[#00ffa3]/30 px-4 py-2 rounded-full text-[#00ffa3]/80 text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 shadow-[0_0_15px_rgba(0,255,163,0.1)]">
            <RefreshCcw size={12} className="animate-spin opacity-80" />
            Reconectando
         </div>
      )}
      {/* Creator/God Panel Trigger Button */}
      {isCreator && hasInteracted && (
        <div className={cn(
          "absolute top-36 right-4 md:top-40 md:right-10 z-50 flex flex-col items-center gap-3 transition-all duration-1000",
          isCurrentlyIdle || uiHiddenLevel === 2 || !showUiControls ? "opacity-0 invisible pointer-events-none" : "opacity-100 visible pointer-events-auto"
        )}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowGodPanel(!showGodPanel);
            }}
            className={cn(
              "p-3 md:p-4 rounded-full border transition-all duration-300 flex items-center justify-center backdrop-blur-sm shadow-[0_0_15px_rgba(0,255,163,0.2)] cursor-pointer",
              showGodPanel
                ? "bg-[#00ffa3]/30 border-[#00ffa3] text-[#00ffa3] scale-110 shadow-[0_0_20px_rgba(0,255,163,0.5)]"
                : "bg-[#0a0e27]/60 border-[#00ffa3]/30 text-[#00ffa3]/70 hover:text-[#00ffa3] hover:border-[#00ffa3]/50 hover:bg-[#0a0e27]/80"
            )}
            title="Motor de Configuração Dinâmica (God Mode)"
          >
            <ShieldCheck size={24} className="md:w-6 md:h-6 opacity-90 animate-pulse" />
          </button>
        </div>
      )}

      {/* Creator/God Master Config Panel Overlay for clicking outside */}
      {isCreator && showGodPanel && uiHiddenLevel !== 2 && (
        <div 
          className="fixed inset-0 z-[999] bg-transparent"
          onClick={() => setShowGodPanel(false)}
        />
      )}

      {/* Creator/God Master Config Panel (Sidebar) */}
      <GodPanel 
        auth={authHook} 
        isCreator={isCreator} 
        isAdmin={isAdmin}
        handleSaveBrandIdentity={handleSaveBrandIdentity}
        saveSettings={saveSettings}
        resetSettings={resetSettings}
        isSaving={isSaving}
      />


      {/* Confirm Dialog Modal */}
      <ConfirmDialogModal
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      {/* Edit System User Modal */}
      <EditUserModal
        editingSystemUser={editingSystemUser}
        setEditingSystemUser={setEditingSystemUser}
        handleSaveUserEdit={handleSaveUserEdit}
      />

      {/* Interactive HUD Controls (Eye & Markets Buttons) */}
      <InteractiveHUDControls
        hasInteracted={hasInteracted}
        uiHiddenLevel={uiHiddenLevel}
        setUiHiddenLevel={setUiHiddenLevel}
        isCurrentlyIdle={isCurrentlyIdle}
        showUiControls={showUiControls}
        isEyeFlashing={isEyeFlashing}
        setIsEyeFlashing={setIsEyeFlashing}
        lastManualUiShowTimeRef={lastManualUiShowTimeRef}
        showMarketsPanel={showMarketsPanel}
        setShowMarketsPanel={setShowMarketsPanel}
        favorites={favorites}
        setActiveMarketCategory={setActiveMarketCategory}
      />

      {/* Aviso discreto enquanto o histórico antigo é buscado/processado */}
      <HistoryLoadingIndicator isLoading={isLoadingHistory && !loadBackgroundRef.current && !waitingAtEdge} />
      {/* F3e C4/C5: uma mensagem de histórico por vez, centralizada (com a MÁQUINA DO TEMPO armada vai abaixo da pílula) */}
      {!timeBannerOn && <HistoryMessage msg={histMsg} onRetry={retryHistory} />}
      {DBG_ENABLED && (
        <DebugOverlay
          nav={navRef.current!}
          targetHistoryOffset={targetHistoryOffset}
          historyOffsetRef={historyOffsetRef}
          totalPoints={navTotalPoints}
          hasReachedHistoryEnd={hasReachedHistoryEnd}
          isLoadingHistory={isLoadingHistory}
          isReturningToLive={isReturningToLive}
          processedData={processedData}
        />
      )}

      {/* Main 3D Trading Canvas */}
      
        {/* Time Travel Armed HUD Indicator */}
        {timeBannerOn && (
          <div className="absolute inset-0 pointer-events-none z-[100] flex flex-col items-center justify-start pt-32">
            <div className="absolute inset-0 border-[4px] border-[#00d9ff]/30 shadow-[inset_0_0_50px_rgba(0,217,255,0.15)] animate-pulse rounded-lg" />
            <div className="bg-[#0a0e27]/90 backdrop-blur-md border border-[#00d9ff] text-[#00d9ff] px-8 py-3 rounded-full font-bold tracking-[0.2em] text-xs md:text-sm shadow-[0_0_30px_rgba(0,217,255,0.5)] animate-bounce flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-[#00d9ff] animate-ping" />
              MÁQUINA DO TEMPO ARMADA - ARRASTE PARA NAVEGAR
              <span className="w-2 h-2 rounded-full bg-[#00d9ff] animate-ping" />
            </div>
            <HistoryMessage inline msg={histMsg} onRetry={retryHistory} />
          </div>
        )}

<Profiler
        id="MainTradingCanvas"
        onRender={(_id, _phase, actualDuration) => {
          appProfiler.record('React:MainTradingCanvas:ActualRenderTime', actualDuration);
        }}
      >
        <MainTradingCanvas
          onOpenMidWallChart={handleOpenMidWallChart}
          onOpenBackWallChart={handleOpenBackWallChart}
          onOpenFloorChart={handleOpenFloorChart}
          onOpenSideChart={handleOpenSideChart}
          isBackWallChartOpen={isBackWallChartOpen}
          isFloorChartOpen={isFloorChartOpen}
          isSideChartOpen={isSideChartOpen}
          isLoadingData={isLoadingData}
          isLoadingDataRef={isLoadingDataRef}
          keepVisibleWhileLoading={switching}
          seriesId={seriesOf(processedData)}
          interactionMode={interactionMode}
          isCrosshairPinned={isCrosshairPinned}
          setIsCrosshairActive={setIsCrosshairActive}
          setSelectedZoneId={setSelectedZoneId}
          handleUserInteraction={handleUserInteraction}
          controlRotateSpeed={controlRotateSpeed}
          controlPanSpeed={controlPanSpeed}
          controlZoomSpeed={controlZoomSpeed}
          dataToRender={processedData}
          navTotalPoints={navTotalPoints}
          scaleResetKey={`${symbol}|${timeframe}|${useRealData ? 'real' : 'sim'}`}
          isReturningToLive={isReturningToLive}
          setIsReturningToLive={setIsReturningToLive} setHistoryOffset={setHistoryOffset} targetHistoryOffset={targetHistoryOffset}
          historyOffsetRef={historyOffsetRef}
          hasReachedHistoryEnd={hasReachedHistoryEnd}
          onOldestEdge={onOldestEdge}
          processedData={processedData}
          show3D={show3D}
          backwallStyle={backwallStyle}
          showShadowFloor={showShadowFloor}
          showShadowSide={showShadowSide}
          showMidLineWall={showMidLineWall}
          setShowMidLineWall={setShowMidLineWall}
          midLineWallColor={midLineWallColor}
          midLineWallOpacity={midLineWallOpacity}
          autoRotate={autoRotate}
          isCrosshairActive={isCrosshairActive}
          crosshairRef={crosshairRef}
          useRealData={useRealData}
          timeframe={timeframe}
          cameraTweenTarget={cameraTweenTarget}
          setCameraTweenTarget={setCameraTweenTarget}
          controlsTweenTarget={controlsTweenTarget}
          setControlsTweenTarget={setControlsTweenTarget}
          cameraStates={cameraStates}
          saveCameraState={saveCameraState}
          zones={zones}
          setZones={setZones}
          selectedZoneId={selectedZoneId}
          setInteractionMode={setInteractionMode}
          timeSensitivity={timeSensitivity}
          radialMenuPos={radialMenuPos}
          setIsInitialGlideComplete={setIsInitialGlideComplete}
          isInitialGlideComplete={isInitialGlideComplete}
          isCurrentlyIdle={isCurrentlyIdle}
          width3D={width3D}
          heightScale3D={heightScale3D}
          zLimit3D={zLimit3D}
          isCreator={isCreator}
          showPerfPanel={showPerfPanel}
        />
      </Profiler>

      {/* Header Terminal Component with native React Profiler */}
      <Profiler
        id="HeaderTerminal"
        onRender={(_id, _phase, actualDuration) => {
          appProfiler.record('React:HeaderTerminal:ActualRenderTime', actualDuration);
        }}
      >
        <HeaderTerminal
          uiHiddenLevel={uiHiddenLevel}
          showUiControls={showUiControls}
          useRealData={useRealData}
          isEditingSymbol={isEditingSymbol}
          setIsEditingSymbol={setIsEditingSymbol}
          symbol={symbol}
          setSymbol={handleUserSetSymbol}
          timeframe={timeframe}
          showMobileData={showMobileData}
          setShowMobileData={setShowMobileData}
          lastPrice={lastPrice}
          priceChange={priceChange}
          currentMomentum={currentMomentum}
          candleCloseTime={candleCloseTime}
          latencyMs={latencyMs}
          isFirestoreOffline={isFirestoreOffline}
          user={user}
          setShowLogoutConfirm={setShowLogoutConfirm}
          loginWithGoogle={loginWithGoogle}
          showLogoutConfirm={showLogoutConfirm}
          handleLogout={handleLogout}
          setShowMobileSettings={setShowMobileSettings}
          setRadialMenuPos={setRadialMenuPos}
        />
      </Profiler>

      {/* Footer Terminal Component */}
      <FooterTerminal
        historyOffset={historyOffset}
        uiHiddenLevel={uiHiddenLevel}
        showUiControls={showUiControls}
        interactionMode={interactionMode}
        setInteractionMode={setInteractionMode}
        isReturningToLive={isReturningToLive}
        setIsReturningToLive={setIsReturningToLive}
                setHistoryOffset={setHistoryOffset}
        targetHistoryOffset={targetHistoryOffset}
        returnSensitivity={returnSensitivity}
        setReturnSensitivity={setReturnSensitivity as any}
        timeSensitivity={timeSensitivity}
        setTimeSensitivity={setTimeSensitivity as any}
        setIsCrosshairActive={setIsCrosshairActive}
        isCrosshairPinned={isCrosshairPinned}
        cameraTweenTarget={cameraTweenTarget}
        setCameraTweenTarget={setCameraTweenTarget}
        setControlsTweenTarget={setControlsTweenTarget}
        cameraViewMode={cameraViewMode}
        setCameraViewMode={setCameraViewMode}
        autoRotate={autoRotate}
        setAutoRotate={setAutoRotate}
        setIsCurrentlyIdle={setIsCurrentlyIdle}
        idleTimerRef={idleTimerRef}
        onExitHistory={() => histEvent('x')}
      />

      {/* AI Analysis Modal Overlay */}
      <AiAnalysisModal
        isAIPanelOpen={isAIPanelOpen}
        setIsAIPanelOpen={setIsAIPanelOpen}
        uiHiddenLevel={uiHiddenLevel}
        isAiLoading={isAiLoading}
        aiAnalysis={aiAnalysis}
        requestAiAnalysis={requestAiAnalysis}
        processedData={processedData}
      />

      {/* Markets Modal Overlay */}
      <MarketsPanel
        showMarketsPanel={showMarketsPanel}
        setShowMarketsPanel={setShowMarketsPanel}
        favorites={favorites}
        activeMarketCategory={activeMarketCategory}
        setActiveMarketCategory={setActiveMarketCategory}
        symbol={symbol}
        setSymbol={handleUserSetSymbol}
        setUseRealData={setUseRealData}
        toggleFavorite={toggleFavorite}
        user={user}
      />

      {/* Settings Modal / Sidebar */}
      <SettingsSidebar
        showMobileSettings={showMobileSettings}
        setShowMobileSettings={setShowMobileSettings}
        isAdmin={isAdmin}
        activeSettingsTab={activeSettingsTab}
        setActiveSettingsTab={setActiveSettingsTab}
        setSaveMessage={setSaveMessage}
        saveMessage={saveMessage}

        leadName={leadName}
        setLeadName={setLeadName}
        leadEmail={leadEmail}
        setLeadEmail={setLeadEmail}
        leadPhone={leadPhone}
        setLeadPhone={setLeadPhone}
        leadCompany={leadCompany}
        setLeadCompany={setLeadCompany}
        leadDetails={leadDetails}
        setLeadDetails={setLeadDetails}
        isSubmittingLead={isSubmittingLead}
        leadSuccessMsg={leadSuccessMsg}
        setLeadSuccessMsg={setLeadSuccessMsg}
        submitLead={submitLead}

        useRealData={useRealData}
        setUseRealData={setUseRealData}
        isLoadingData={isLoadingData}
        dataError={dataError}

        controlRotateSpeed={controlRotateSpeed}
        setControlRotateSpeed={setControlRotateSpeed}
        controlPanSpeed={controlPanSpeed}
        setControlPanSpeed={setControlPanSpeed}
        controlZoomSpeed={controlZoomSpeed}
        setControlZoomSpeed={setControlZoomSpeed}
        setColorUp={setColorUp}
        setColorDown={setColorDown}
        show3D={show3D}
        setShow3D={setShow3D}
        setColorIntensity={setColorIntensity}
        setMain3DOpacity={setMain3DOpacity}
        showShadowFloor={showShadowFloor}
        setShowShadowFloor={setShowShadowFloor}
        setFloorChartOpacity={setFloorChartOpacity}
        setFloorChartLineWidth={setFloorChartLineWidth}
        showShadowSide={showShadowSide}
        setShowShadowSide={setShowShadowSide}
        setSideChartOpacity={setSideChartOpacity}
        setSideChartLineWidth={setSideChartLineWidth}
        backwallStyle={backwallStyle}
        setBackwallStyle={setBackwallStyle}
        setBackChartOpacity={setBackChartOpacity}
        showMidLineWall={showMidLineWall}
        setShowMidLineWall={setShowMidLineWall}
        midLineWallColor={midLineWallColor}
        setMidLineWallColor={setMidLineWallColor}
        midLineWallOpacity={midLineWallOpacity}
        setMidLineWallOpacity={setMidLineWallOpacity}
        setShowGrid={setShowGrid}
        setGridColor={setGridColor}
        setGridOpacity={setGridOpacity}
        setGridThickness={setGridThickness}
        setShowSMA10={setShowSMA10}
        setSma10Period={setSma10Period}
        setShowSMA20={setShowSMA20}
        setSma20Period={setSma20Period}
        setShowSMA50={setShowSMA50}
        setSma50Period={setSma50Period}
        setShowConnectors={setShowConnectors}
        setConnectorsOpacity={setConnectorsOpacity}
        setConnectorsThickness={setConnectorsThickness}
        setShowPeriodSeparators={setShowPeriodSeparators}
        setPeriodColor={setPeriodColor}
        setPeriodOpacity={setPeriodOpacity}
        setShowPeriodLabels={setShowPeriodLabels}

        lockedSliders={lockedSliders}
        setLockedSliders={setLockedSliders}

        user={user}
        authToken={authToken}
        saveSettings={saveSettings}
        syncWorkspaceToCloud={syncWorkspaceToCloud}
        loadWorkspaceFromCloud={loadWorkspaceFromCloud}
        resetSettings={resetSettings}
        isSaving={isSaving}

      />










              


















      {/* Info Panel (Market Data) Overlay */}
      <MarketDataOverlay
        showMobileData={showMobileData}
        setShowMobileData={setShowMobileData}
        isTutorialMode={isTutorialMode}
        currentPrice={currentPrice}
        periodHigh={periodHigh}
        periodLow={periodLow}
        periodChange={periodChange}
        dataToRender={dataToRender}
        timeframe={timeframe}
        timeframeNames={timeframeNames}
      />

      {/* Instructions Tutorial Overlay (Bottom Center) */}
      {isTutorialMode && interactionMode === 'orbit' && (
        <div className="absolute bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none select-none">
          <div className="bg-[#0a0e27]/90 backdrop-blur-md border border-[#00d9ff]/35 px-5 py-3 rounded-full text-center max-w-[90vw] md:max-w-[80vw] shadow-lg shadow-[#00d9ff]/10 animate-pulse">
            <p className="text-[9px] md:text-[11px] uppercase tracking-[1px] md:tracking-[2px] text-[#00d9ff] font-bold leading-none">
              Girador: 1 Dedo | Voltar: 2 Dedos | Zoom: Pinça | 2 Toques: Menu
            </p>
          </div>
        </div>
      )}

      {/* Interactive Mode HUD (Top Center) */}
      <InteractiveModeHUD
        interactionMode={interactionMode}
        setInteractionMode={setInteractionMode}
        setIsCrosshairActive={setIsCrosshairActive}
      />

      {/* User Feedbacks Modal & Delete Feedback Modal */}
      <UserFeedbacksModal
        userFeedbacksModalId={userFeedbacksModalId}
        setUserFeedbacksModalId={setUserFeedbacksModalId}
        feedbacksList={feedbacksList}
        isAdmin={isAdmin}
        feedbackToDelete={feedbackToDelete}
        setFeedbackToDelete={setFeedbackToDelete}
        confirmDeleteFeedback={confirmDeleteFeedback}
      />

        
      {user && <AsyncFeedbackWidget />}
      <RadialMenu 
        
        position={radialMenuPos} 
        onClose={() => {
          setRadialMenuPos(null);
          setActiveGesture(null);
        }} 
        timeframe={timeframe} 
        setTimeframe={handleUserSetTimeframe} 
        interactionMode={interactionMode} 
        setInteractionMode={setInteractionMode} 
        setZones={setZones}
        setSelectedZoneId={setSelectedZoneId}
        visibleTimeframes={visibleTimeframes}
        setVisibleTimeframes={handleUserSetVisibleTimeframes}
        isLoadingData={isLoadingData}
      />
      </div>
    </>
  );
}





